import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Wallet,
  Activity,
  Clock,
  Home as HomeIcon, // eslint-disable-line no-unused-vars
  ChevronRight,
  X,
  CheckCircle2,
  Phone,
  MapPin,
  Ruler,
  BedDouble,
  UserCheck,
  Sparkles,
  RefreshCw,
  Eye,
  Share2,
  TrendingUp,
  Award,
  Download,
  Plus,
  Trash2,
  Pencil,
  Search as SearchIcon,
  Megaphone,
  Copy,
  Image as PhotoIcon,
  Bell,
  ExternalLink,
  CreditCard,
  Percent,
  ToggleLeft,
  ToggleRight,
  Save,
  AlertTriangle,
  Calendar,
  ShieldCheck,
  Webhook,
  Wifi,
  KeyRound,
  Lock,
  EyeOff,
  LogOut,
  Loader2,
  Check,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import BrandLogo from "@/components/BrandLogo";
import { DrawingFilesCard } from "@/components/DrawingFilesCard";
import { useAuth } from "@/auth/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  ProfessionalsModerationPanel,
  InquiriesModerationPanel,
} from "@/components/admin/ProsModerationPanels";
import StudioRequestsPanel from "@/components/admin/StudioRequestsPanel";
import BudgetEMISettingsPanel from "@/components/admin/BudgetEMISettingsPanel";
import SampleDesignPanel from "@/components/admin/SampleDesignPanel";
import TestModeBanner from "@/components/admin/TestModeBanner";
import OpsVerificationPanel from "@/components/admin/OpsVerificationPanel";
import Msg91DiagnosticPanel from "@/components/admin/Msg91DiagnosticPanel";
import { adminGet } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMNS = [
  { id: "new", title: "New Orders", title_hi: "नए ऑर्डर", tone: "navy" },
  { id: "assigned", title: "Assigned", title_hi: "असाइन्ड", tone: "indigo" },
  { id: "in_review", title: "In Review", title_hi: "रिव्यू में", tone: "gold" },
  { id: "delivered", title: "Delivered", title_hi: "डिलीवर्ड", tone: "neem" },
];

const PACKAGE_LABEL = {
  basic: { label: "Basic 2D", color: "bg-navy-50 text-navy border-navy/15" },
  standard: { label: "Standard 3D", color: "bg-blue-50 text-blue-700 border-blue-200" },
  full: { label: "Full Set", color: "bg-amber-50 text-amber-800 border-amber-200" },
  premium: { label: "Premium", color: "bg-gold/15 text-gold-dark border-gold/30" },
};

const formatINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const StatCard = ({ icon, label, value, accent = "navy", testId }) => {
  const accents = {
    navy: "bg-navy text-white border-navy-600",
    gold: "bg-gold text-navy border-gold-dark",
    white: "bg-white text-navy border-border",
  };
  return (
    <div
      className={`rounded-2xl p-4 sm:p-5 border shadow-sm flex items-center gap-3 ${accents[accent]}`}
      data-testid={testId}
    >
      <div
        className={`h-11 w-11 rounded-xl grid place-items-center shrink-0 ${
          accent === "navy"
            ? "bg-gold/15 text-gold border border-gold/30"
            : accent === "gold"
            ? "bg-navy/10 text-navy"
            : "bg-navy-50 text-navy"
        }`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div
          className={`text-[11px] uppercase tracking-[0.18em] font-bold ${
            accent === "navy" ? "text-gold" : accent === "gold" ? "text-navy/80" : "text-muted-foreground"
          }`}
        >
          {label}
        </div>
        <div className="text-xl sm:text-2xl font-extrabold tabular-nums truncate">
          {value}
        </div>
      </div>
    </div>
  );
};

const OrderCard = ({ order, onOpen, onDragStart }) => {
  const pkg = PACKAGE_LABEL[order.package] || PACKAGE_LABEL.basic;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, order)}
      onClick={() => onOpen(order)}
      data-testid={`order-card-${order.id}`}
      className="group cursor-grab active:cursor-grabbing rounded-2xl bg-white border border-border p-3.5 shadow-sm hover:shadow-md hover:border-navy/30 transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[15px] font-bold text-navy truncate">
            {order.customer_name}
          </div>
          <div className="text-[12px] text-muted-foreground flex items-center gap-1 mt-0.5">
            <MapPin className="h-3 w-3" /> {order.customer_city}
          </div>
        </div>
        <span
          className={`text-[10px] font-bold px-2 py-1 rounded-full border whitespace-nowrap ${pkg.color}`}
        >
          {pkg.label}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
        <div className="flex items-center gap-1.5 text-foreground">
          <Ruler className="h-3.5 w-3.5 text-navy" />
          {order.plot_size}
        </div>
        <div className="flex items-center gap-1.5 text-foreground">
          <BedDouble className="h-3.5 w-3.5 text-navy" />
          {order.rooms}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div className="text-[12px] text-muted-foreground truncate">
          {order.architect_name ? (
            <span className="inline-flex items-center gap-1">
              <UserCheck className="h-3.5 w-3.5 text-neem" />
              {order.architect_name}
            </span>
          ) : (
            <span className="italic">Unassigned</span>
          )}
        </div>
        <div className="text-[13px] font-bold text-gold-dark">{formatINR(order.price)}</div>
      </div>
    </div>
  );
};

const Column = ({ col, orders, onDropTo, onOpen, onDragStart }) => {
  const [over, setOver] = useState(false);
  const toneRing = {
    navy: "ring-navy/40",
    indigo: "ring-blue-400/40",
    gold: "ring-gold/40",
    neem: "ring-emerald-400/40",
  }[col.tone];
  const headDot = {
    navy: "bg-navy",
    indigo: "bg-blue-500",
    gold: "bg-gold",
    neem: "bg-neem",
  }[col.tone];

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onDropTo(col.id, e);
      }}
      data-testid={`column-${col.id}`}
      className={`flex flex-col rounded-2xl bg-sand-alt/60 border border-border p-3 min-h-[480px] ${
        over ? `ring-4 ${toneRing}` : ""
      }`}
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${headDot}`} />
          <h3 className="text-[14px] font-bold text-navy uppercase tracking-wider">
            {col.title}
          </h3>
        </div>
        <span className="text-xs font-bold bg-white border border-border rounded-full px-2 py-0.5 text-navy">
          {orders.length}
        </span>
      </div>
      <div className="flex flex-col gap-2.5 flex-1">
        {orders.length === 0 && (
          <div className="text-center text-[12px] text-muted-foreground py-8 italic">
            Drop cards here
          </div>
        )}
        {orders.map((o) => (
          <OrderCard
            key={o.id}
            order={o}
            onOpen={onOpen}
            onDragStart={onDragStart}
          />
        ))}
      </div>
    </div>
  );
};

const OrderDetailPanel = ({ order, architects, onClose, onAssign, onApprove, onMove }) => {
  if (!order) return null;
  const pkg = PACKAGE_LABEL[order.package] || PACKAGE_LABEL.basic;
  const waLink = `https://wa.me/${order.customer_whatsapp.replace(/[^\d]/g, "")}`;

  return (
    <div
      className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose}
      data-testid="order-detail-overlay"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white shadow-2xl border-l-2 border-gold flex flex-col animate-fade-up"
        data-testid="order-detail-panel"
      >
        <div className="bg-navy text-white p-5 relative">
          <button
            onClick={onClose}
            data-testid="order-detail-close"
            className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="text-[11px] uppercase tracking-wider text-gold font-bold">
            Order #{order.id}
          </div>
          <h2 className="mt-1 text-2xl font-extrabold">{order.customer_name}</h2>
          <div className="mt-1 text-white/70 text-sm flex items-center gap-1">
            <MapPin className="h-4 w-4" /> {order.customer_city}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${pkg.color}`}>
              {pkg.label}
            </span>
            <span className="text-gold font-extrabold text-lg">
              {formatINR(order.price)}
            </span>
          </div>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <DetailRow icon={<Phone className="h-4 w-4" />} label="WhatsApp">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-whatsapp hover:underline font-semibold"
              data-testid="order-customer-whatsapp"
            >
              {order.customer_whatsapp}
            </a>
          </DetailRow>
          <div className="grid grid-cols-2 gap-3">
            <DetailRow icon={<Ruler className="h-4 w-4" />} label="Plot">
              {order.plot_size}
            </DetailRow>
            <DetailRow icon={<BedDouble className="h-4 w-4" />} label="Rooms">
              {order.rooms}
            </DetailRow>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5">
              Assigned Design Mitra
            </div>
            <select
              value={order.architect_id || ""}
              onChange={(e) => onAssign(order.id, e.target.value || null)}
              data-testid="order-architect-select"
              className="w-full rounded-xl border-2 border-border bg-white px-3 py-2.5 text-sm font-medium focus:border-navy focus:outline-none"
            >
              <option value="">— Unassigned —</option>
              {architects.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} • {a.city} • ⭐ {a.rating}
                </option>
              ))}
            </select>
          </div>

          {order.notes && (
            <div className="rounded-xl bg-sand-alt border border-border p-3">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-1">
                Internal notes
              </div>
              <div className="text-sm text-foreground">{order.notes}</div>
            </div>
          )}

          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5">
              Move to status
            </div>
            <div className="grid grid-cols-2 gap-2">
              {COLUMNS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => onMove(order.id, c.id)}
                  data-testid={`order-move-${c.id}`}
                  disabled={order.status === c.id}
                  className={`text-[12px] font-bold py-2 rounded-xl border-2 transition-all ${
                    order.status === c.id
                      ? "bg-navy text-gold border-navy cursor-default"
                      : "bg-white text-navy border-border hover:border-navy/40"
                  }`}
                >
                  {c.title}
                </button>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-muted-foreground">
            Created: {new Date(order.created_at).toLocaleString("en-IN")}
            {order.delivered_at && (
              <>
                <br />
                Delivered: {new Date(order.delivered_at).toLocaleString("en-IN")}
              </>
            )}
          </div>
        </div>

        {order.status === "in_review" && (
          <div className="p-4 bg-gradient-to-t from-sand-alt to-transparent border-t border-border">
            <button
              onClick={() => onApprove(order.id)}
              data-testid="order-approve-btn"
              className="w-full min-h-[60px] rounded-2xl bg-whatsapp text-white font-extrabold text-[15px] flex items-center justify-center gap-2 shadow-[0_12px_28px_rgba(37,211,102,0.35)] active:scale-[0.99] animate-pulse-soft"
            >
              <FaWhatsapp className="h-5 w-5" />
              Approve for WhatsApp
              <ChevronRight className="h-5 w-5" />
            </button>
            <div className="mt-2 text-center text-[11px] text-muted-foreground">
              Marks the order delivered & sends naksha to customer.
            </div>
          </div>
        )}
      </aside>
    </div>
  );
};

const DetailRow = ({ icon, label, children }) => (
  <div className="rounded-xl bg-sand-alt/60 border border-border px-3 py-2.5">
    <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-bold flex items-center gap-1.5">
      <span className="text-navy">{icon}</span>
      {label}
    </div>
    <div className="mt-0.5 text-sm font-semibold text-foreground">{children}</div>
  </div>
);

const AdminDashboard = () => {
  const { adminUser: user, logoutAdmin: logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };
  const [orders, setOrders] = useState([]);
  const [architects, setArchitects] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [active, setActive] = useState(null); // open order detail
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);

  const showToast = useCallback((msg, tone = "success") => {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 2400);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [o, a, s, an] = await Promise.all([
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/admin/architects`),
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/analytics`),
      ]);
      setOrders(o.data);
      setArchitects(a.data);
      setStats(s.data);
      setAnalytics(an.data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error(e);
      showToast("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Establish "admin session" — drives the unrestricted Design Library mode.
  // Cleared on the special /store?exit_admin=1 deep-link rendered inside the
  // store, so a founder can drop back into a pure customer view while testing.
  useEffect(() => {
    try {
      window.sessionStorage.setItem("mn_admin", "1");
    } catch (err) {
      /* ignore — private mode */
      if (process.env.NODE_ENV !== "production") {
        console.warn("admin flag set failed:", err?.message || err);
      }
    }
  }, []);

  const grouped = useMemo(() => {
    const g = { new: [], assigned: [], in_review: [], delivered: [] };
    for (const o of orders) (g[o.status] || g.new).push(o);
    return g;
  }, [orders]);

  const onDragStart = (e, order) => {
    e.dataTransfer.setData("text/plain", order.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const moveOrder = useCallback(
    async (id, status) => {
      try {
        const { data } = await axios.patch(`${API}/admin/orders/${id}`, { status });
        setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
        setActive((prev) => (prev && prev.id === id ? data : prev));
        showToast(`Moved to "${COLUMNS.find((c) => c.id === status).title}"`);
        // refresh stats async
        axios.get(`${API}/admin/stats`).then((r) => setStats(r.data));
      } catch (e) {
        showToast(e?.response?.data?.detail || "Update failed", "error");
      }
    },
    [showToast]
  );

  const onDropTo = (colId, e) => {
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    const order = orders.find((o) => o.id === id);
    if (!order || order.status === colId) return;
    moveOrder(id, colId);
  };

  const onAssign = async (id, architectId) => {
    try {
      const { data } = await axios.patch(`${API}/admin/orders/${id}`, {
        architect_id: architectId,
      });
      setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
      setActive((prev) => (prev && prev.id === id ? data : prev));
      showToast(architectId ? "Design Mitra assigned" : "Design Mitra cleared");
    } catch (e) {
      showToast("Assignment failed", "error");
    }
  };

  const onApprove = async (id) => {
    try {
      const { data } = await axios.post(`${API}/admin/orders/${id}/approve`);
      setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
      setActive(null);
      showToast("Approved & delivered on WhatsApp 🎉");
      axios.get(`${API}/admin/stats`).then((r) => setStats(r.data));
    } catch (e) {
      showToast(e?.response?.data?.detail || "Approval failed", "error");
    }
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-root">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-navy text-white border-b-4 border-gold shadow-[0_8px_24px_-12px_rgba(11,37,69,0.4)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-home-link"
              aria-label="Back to landing"
              className="inline-flex items-center bg-white rounded-lg px-2 py-1 shadow-sm"
            >
              <BrandLogo size="sm" asLink={false} withTagline lang="en" testId="admin-brand-logo" />
            </Link>
            <div className="leading-tight">
              <div className="text-[12px] uppercase tracking-[0.2em] text-gold font-bold">
                Admin Dashboard
              </div>
              <div className="text-[15px] font-extrabold tracking-tight">
                Operations
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/store"
              data-testid="admin-open-bazaar"
              title="Open Design Library in Admin Mode"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gold text-navy text-sm font-extrabold border-2 border-gold-dark hover:bg-gold-dark hover:text-white transition-colors"
            >
              <Eye className="h-4 w-4" />
              Design Library (Admin)
            </Link>
            <NotificationBell onToast={showToast} />
            <button
              onClick={refresh}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold border border-white/15"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            <div className="hidden md:flex items-center gap-2 pl-2 ml-1 border-l border-white/15">
              <div className="leading-tight text-right">
                <div className="text-[11px] uppercase tracking-wider text-gold font-bold">
                  Signed in
                </div>
                <div
                  className="text-[12px] font-extrabold truncate max-w-[160px]"
                  data-testid="admin-current-email"
                  title={user?.email}
                >
                  {user?.email || ""}
                </div>
              </div>
              <button
                onClick={handleLogout}
                data-testid="admin-logout"
                title="Sign out"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500/15 hover:bg-red-500/30 text-red-200 hover:text-white text-sm font-bold border border-red-400/30 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-mobile"
              title="Sign out"
              className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-xl bg-red-500/15 hover:bg-red-500/30 text-red-200 border border-red-400/30"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <TestModeBanner />

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard
            testId="stat-revenue"
            icon={<Wallet className="h-5 w-5" />}
            label="Total revenue"
            value={stats ? formatINR(stats.revenue) : "—"}
            accent="navy"
          />
          <StatCard
            testId="stat-active"
            icon={<Activity className="h-5 w-5" />}
            label="Active orders"
            value={stats ? stats.active_orders : "—"}
            accent="gold"
          />
          <StatCard
            testId="stat-avg-time"
            icon={<Clock className="h-5 w-5" />}
            label="Avg delivery"
            value={stats ? `${stats.avg_delivery_hours} h` : "—"}
            accent="white"
          />
          <StatCard
            testId="stat-delivered"
            icon={<CheckCircle2 className="h-5 w-5" />}
            label="Delivered"
            value={stats ? stats.delivered_orders : "—"}
            accent="white"
          />
        </section>

        {/* Tip */}
        <div className="mt-5 rounded-2xl bg-gold/15 border border-gold/30 px-4 py-2.5 text-[13px] text-navy flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-gold-dark" />
          <span>
            <b>Tip:</b> Drag any card across columns to update its status. Click a
            card for full customer & Design Mitra details.
          </span>
        </div>

        {/* Launch Day Analytics */}
        <AnalyticsSection analytics={analytics} />

        {/* Kanban */}
        <section
          className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4"
          data-testid="kanban-board"
        >
          {COLUMNS.map((col) => (
            <Column
              key={col.id}
              col={col}
              orders={grouped[col.id] || []}
              onDropTo={onDropTo}
              onOpen={setActive}
              onDragStart={onDragStart}
            />
          ))}
        </section>

        {loading && (
          <div className="mt-8 text-center text-muted-foreground" data-testid="admin-loading">
            Loading…
          </div>
        )}
      </main>

      <OrderDetailPanel
        order={active}
        architects={architects}
        onClose={() => setActive(null)}
        onAssign={onAssign}
        onApprove={onApprove}
        onMove={moveOrder}
      />

      <main className="mx-auto max-w-7xl px-4 sm:px-6 pb-10">
        <SystemHealthWidget />
        <SecurePaymentSettingsPanel onToast={showToast} />
        <OpsVerificationPanel />
        <Msg91DiagnosticPanel />
        <PaymentPricingPanel onToast={showToast} />
        <SampleDesignPanel onToast={showToast} />
        <BudgetEMISettingsPanel onToast={showToast} />
        <MarketingToolkit onToast={showToast} />
        <LibraryHealthDashboard onToast={showToast} />
        <StudioRequestsPanel onToast={showToast} />
        <LibraryManager onToast={showToast} />
        <ProfessionalsModerationPanel onToast={showToast} />
        <InquiriesModerationPanel onToast={showToast} />
      </main>

      {toast && (
        <div
          data-testid="admin-toast"
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-4 py-2.5 rounded-full shadow-lg text-sm font-bold ${
            toast.tone === "error"
              ? "bg-red-600 text-white"
              : "bg-navy text-gold border border-gold/30"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

const AnalyticsSection = ({ analytics }) => {
  if (!analytics) {
    return (
      <section className="mt-5 rounded-2xl bg-white border border-border p-5 text-center text-muted-foreground" data-testid="analytics-loading">
        Loading launch-day analytics…
      </section>
    );
  }
  const fmt = (n) => Number(n).toLocaleString("en-IN");
  const { page_views, whatsapp_clicks, link_shares, geo, funnel, referrals } = analytics;
  const maxGeo = Math.max(...geo.map((g) => g.clicks), 1);
  const maxFunnel = Math.max(...funnel.map((f) => f.count), 1);
  const maxRef = Math.max(...referrals.map((r) => r.clicks), 1);

  return (
    <section className="mt-5" data-testid="analytics-section">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">
            Launch Day Analytics
          </div>
          <h2 className="mt-1 text-xl sm:text-2xl font-extrabold text-navy">
            Real-time impact of your WhatsApp shares
          </h2>
        </div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-700 text-[11px] font-bold">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          LIVE
        </span>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiTile testId="kpi-page-views" icon={<Eye className="h-5 w-5" />} label="Page views" value={fmt(page_views)} accent="navy" />
        <KpiTile testId="kpi-wa-clicks" icon={<FaWhatsapp className="h-5 w-5" />} label="WhatsApp leads" value={fmt(whatsapp_clicks)} accent="green" />
        <KpiTile testId="kpi-link-shares" icon={<Share2 className="h-5 w-5" />} label="Link shares" value={fmt(link_shares)} accent="gold" />
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Geo */}
        <div className="rounded-2xl bg-white border border-border p-4 shadow-sm" data-testid="analytics-geo">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-navy" />
            <h3 className="font-bold text-navy">Top districts</h3>
          </div>
          <ul className="mt-3 space-y-2">
            {geo.map((g) => (
              <li key={g.district} className="text-[13px]" data-testid={`geo-${g.district.toLowerCase()}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-foreground">
                    {g.district} <span className="text-muted-foreground font-normal">• {g.state}</span>
                  </span>
                  <span className="font-bold text-navy tabular-nums">{g.clicks}</span>
                </div>
                <div className="h-1.5 rounded-full bg-sand-alt overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-navy to-gold"
                    style={{ width: `${(g.clicks / maxGeo) * 100}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Funnel */}
        <div className="rounded-2xl bg-white border border-border p-4 shadow-sm" data-testid="analytics-funnel">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-navy" />
            <h3 className="font-bold text-navy">Conversion funnel</h3>
          </div>
          <div className="mt-3 space-y-3">
            {funnel.map((f, i) => {
              const tone = ["bg-navy text-gold", "bg-whatsapp text-white", "bg-gold text-navy"][i] || "bg-navy text-gold";
              const pct = (f.count / maxFunnel) * 100;
              const dropPct = i > 0 ? Math.round((f.count / funnel[0].count) * 100) : 100;
              return (
                <div key={f.stage} data-testid={`funnel-${f.stage.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="flex items-center justify-between text-[13px] mb-1">
                    <span className="font-bold text-foreground">{f.stage}</span>
                    <span className="font-bold tabular-nums">
                      {fmt(f.count)} <span className="text-[11px] text-muted-foreground font-normal">({dropPct}%)</span>
                    </span>
                  </div>
                  <div
                    className={`h-9 rounded-xl ${tone} flex items-center justify-end px-3 text-[12px] font-bold transition-all`}
                    style={{ width: `${Math.max(pct, 12)}%` }}
                  >
                    {dropPct === 100 ? "Top of funnel" : `${dropPct}%`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Referrals */}
      <div className="mt-4 rounded-2xl bg-white border border-border p-4 shadow-sm" data-testid="analytics-referrals">
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4 text-navy" />
          <h3 className="font-bold text-navy">Top referrers (Mitra & Mistri Sathi)</h3>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
                <th className="text-left py-2 pr-2">Code</th>
                <th className="text-left py-2 pr-2">Name</th>
                <th className="text-left py-2 pr-2">Type</th>
                <th className="text-right py-2 pr-2">Clicks</th>
                <th className="text-right py-2 pr-2">Leads</th>
                <th className="text-right py-2">Orders</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((r, i) => (
                <tr key={r.code} className="border-t border-border" data-testid={`ref-${r.code}`}>
                  <td className="py-2 pr-2 font-mono font-bold text-navy">{r.code}</td>
                  <td className="py-2 pr-2 text-foreground">{r.name}</td>
                  <td className="py-2 pr-2">
                    <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                      r.type === "Design Mitra"
                        ? "bg-gold/15 text-gold-dark border-gold/30"
                        : "bg-navy-50 text-navy border-navy/15"
                    }`}>
                      {r.type}
                    </span>
                  </td>
                  <td className="py-2 pr-2 text-right">
                    <div className="font-bold tabular-nums">{r.clicks}</div>
                    <div className="h-1 mt-1 rounded-full bg-sand-alt overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-navy to-gold" style={{ width: `${(r.clicks / maxRef) * 100}%` }} />
                    </div>
                  </td>
                  <td className="py-2 pr-2 text-right font-bold text-whatsapp tabular-nums">{r.leads}</td>
                  <td className="py-2 text-right font-extrabold text-gold-dark tabular-nums">
                    {r.orders}
                    {i === 0 && <span className="ml-1 text-[10px] align-top">🏆</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const KpiTile = ({ icon, label, value, accent = "navy", testId }) => {
  const accents = {
    navy: "bg-navy text-white border-navy-600",
    green: "bg-whatsapp text-white border-whatsapp-hover",
    gold: "bg-gold text-navy border-gold-dark",
  };
  return (
    <div className={`rounded-2xl p-4 border shadow-sm flex items-center gap-3 ${accents[accent]}`} data-testid={testId}>
      <div className={`h-11 w-11 rounded-xl grid place-items-center shrink-0 ${
        accent === "gold" ? "bg-navy/10 text-navy" : "bg-white/15 text-white"
      }`}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className={`text-[11px] uppercase tracking-[0.18em] font-bold ${
          accent === "gold" ? "text-navy/80" : "text-white/85"
        }`}>{label}</div>
        <div className="text-2xl font-extrabold tabular-nums truncate">{value}</div>
      </div>
    </div>
  );
};

const DB_PAGE_SIZE = 10;

// ============================================================================
//  Admin · Library Health Dashboard
// ----------------------------------------------------------------------------
//  Single-call audit that proves DB ↔ API ↔ Dashboard counts agree, and
//  surfaces designs that need attention (placeholder 3D renders, missing
//  PDFs, etc.) so the founder can fix drift in one place.
// ============================================================================
const HealthStat = ({ label, value, tone = "navy", testId }) => {
  const tones = {
    navy: "bg-navy text-white border-navy-600",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    white: "bg-white text-navy border-border",
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border-2 px-3.5 py-3 ${tones[tone]} shadow-sm`}
    >
      <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold opacity-80">
        {label}
      </div>
      <div className="mt-1 text-2xl font-extrabold tabular-nums">{value}</div>
    </div>
  );
};

const LibraryHealthDashboard = ({ onToast }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all"); // all | placeholder | missing_pdf

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data: payload } = await axios.get(`${API}/admin/library/health`);
      setData(payload);
    } catch (e) {
      onToast("Failed to load library health report", "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [onToast]);

  useEffect(() => {
    load();
  }, [load]);

  const inventory = useMemo(() => {
    if (!data) return [];
    if (filter === "placeholder")
      return data.inventory.filter((r) => r.image_state === "placeholder");
    if (filter === "missing_image")
      return data.inventory.filter((r) => r.image_state === "missing");
    if (filter === "missing_plan_asset")
      return data.inventory.filter((r) => !r.has_any_plan_asset);
    if (filter === "missing_pdf")
      // "Missing PDF" now means "no PDF anywhere" (not just no admin upload).
      return data.inventory.filter((r) => !r.has_any_pdf);
    if (filter === "plan_image_only")
      return data.inventory.filter((r) => !r.has_any_pdf && r.has_any_plan_asset);
    if (filter === "archived")
      return data.inventory.filter((r) => r.is_archived);
    return data.inventory;
  }, [data, filter]);

  if (loading) {
    return (
      <section
        className="mt-5 rounded-2xl bg-white border border-border p-5 text-center text-muted-foreground"
        data-testid="library-health-loading"
      >
        <Loader2 className="inline-block h-4 w-4 mr-2 animate-spin" />
        Auditing Design Library…
      </section>
    );
  }
  if (!data) return null;

  const c = data.counts;
  const generated = data.generated_at
    ? new Date(data.generated_at).toLocaleString("en-IN")
    : "";

  return (
    <section
      className="mt-6 rounded-3xl bg-white border-2 border-navy/10 p-5 sm:p-6 shadow-sm"
      data-testid="library-health-dashboard"
    >
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">
            Admin · Data Health
          </div>
          <h2 className="mt-1 text-xl sm:text-2xl font-extrabold text-navy">
            Design Library — full-visibility audit
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Single-source-of-truth count from MongoDB · refreshed {generated}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          data-testid="library-health-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-navy text-white text-xs font-bold hover:bg-navy-600 disabled:opacity-60"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Headline counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2.5">
        <HealthStat testId="health-total"          label="Total" value={c.total} tone="navy" />
        <HealthStat testId="health-active"         label="Active" value={c.active} tone="green" />
        <HealthStat testId="health-archived"       label="Archived" value={c.archived} tone={c.archived ? "amber" : "white"} />
        <HealthStat testId="health-bestseller"     label="Bestseller" value={c.bestseller} tone="white" />
        <HealthStat testId="health-image-real"     label="Real 3D" value={c.image_real} tone="green" />
        <HealthStat testId="health-image-placeholder" label="Placeholder" value={c.image_placeholder} tone={c.image_placeholder ? "amber" : "white"} />
      </div>

      {/* Secondary counts — customer-deliverable plans + PDF quality. */}
      <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <HealthStat testId="health-image-missing" label="Missing image" value={c.image_missing} tone={c.image_missing ? "red" : "white"} />
        <HealthStat
          testId="health-plan-asset-present"
          label="Plan asset present"
          value={c.plan_asset_present}
          tone={c.plan_asset_missing ? "amber" : "green"}
        />
        <HealthStat
          testId="health-plan-asset-missing"
          label="No plan at all"
          value={c.plan_asset_missing || 0}
          tone={c.plan_asset_missing ? "red" : "white"}
        />
        <HealthStat
          testId="health-pdf-present"
          label="PDF plan"
          value={c.pdf_present}
          tone={c.pdf_present === c.total ? "green" : "white"}
        />
        <HealthStat
          testId="health-plan-image-only"
          label="Image-only plan"
          value={c.plan_image_only || 0}
          tone={c.plan_image_only ? "amber" : "white"}
        />
        <HealthStat
          testId="health-multi-floor"
          label="Multi-floor sets"
          value={c.multi_floor_designs || 0}
          tone="white"
        />
        <HealthStat
          testId="health-admin-pdf-cached"
          label="Admin PDF cached"
          value={c.admin_uploaded_pdf_present || 0}
          tone="white"
        />
        <HealthStat testId="health-tech-missing"  label="Tech-img missing" value={c.technical_img_missing} tone={c.technical_img_missing ? "amber" : "white"} />
      </div>

      {/* Warnings */}
      {data.warnings && data.warnings.length > 0 && (
        <div
          className="mt-4 rounded-2xl border-2 border-amber-200 bg-amber-50 p-3.5"
          data-testid="library-health-warnings"
        >
          <div className="flex items-center gap-2 text-amber-800 text-xs uppercase tracking-wider font-bold mb-2">
            <AlertTriangle className="h-4 w-4" />
            Warnings
          </div>
          <ul className="space-y-1.5">
            {data.warnings.map((w) => (
              <li
                key={w}
                className="text-sm text-amber-900 leading-snug"
                data-testid="library-health-warning-item"
              >
                • {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filter chips */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {[
          { id: "all", label: `All (${c.total})` },
          { id: "placeholder", label: `Placeholder 3D (${c.image_placeholder})` },
          { id: "missing_image", label: `Missing image (${c.image_missing})` },
          { id: "missing_plan_asset", label: `No plan at all (${c.plan_asset_missing || 0})` },
          { id: "missing_pdf", label: `Missing PDF (${c.pdf_missing})` },
          { id: "plan_image_only", label: `Image-only plan (${c.plan_image_only || 0})` },
          { id: "archived", label: `Archived (${c.archived})` },
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            data-testid={`library-health-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${
              filter === f.id
                ? "bg-navy text-white border-navy"
                : "bg-white text-navy border-border hover:border-navy/40"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Inventory table — proof every record is accounted for */}
      <div className="mt-3 overflow-x-auto rounded-2xl border border-border">
        <table className="w-full text-sm" data-testid="library-health-table">
          <thead className="bg-sand-alt text-navy text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 font-bold">#</th>
              <th className="text-left px-3 py-2 font-bold">ID</th>
              <th className="text-left px-3 py-2 font-bold">Name</th>
              <th className="text-left px-3 py-2 font-bold">Size · BHK</th>
              <th className="text-right px-3 py-2 font-bold">Price</th>
              <th className="text-center px-3 py-2 font-bold">3D Image</th>
              <th className="text-center px-3 py-2 font-bold">PDF</th>
              <th className="text-center px-3 py-2 font-bold">Status</th>
            </tr>
          </thead>
          <tbody>
            {inventory.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-3 py-6 text-center text-muted-foreground">
                  No designs match this filter.
                </td>
              </tr>
            ) : (
              inventory.map((row, idx) => (
                <tr
                  key={row.id}
                  data-testid={`library-health-row-${row.id}`}
                  className="border-t border-border/60 hover:bg-sand-alt/40"
                >
                  <td className="px-3 py-2 text-muted-foreground tabular-nums">{idx + 1}</td>
                  <td className="px-3 py-2 font-mono text-[12px] text-navy/80">{row.id}</td>
                  <td className="px-3 py-2 font-semibold text-navy">{row.name}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {row.size_label}{row.bhk ? ` · ${row.bhk}BHK` : ""}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold text-navy">
                    ₹{Number(row.price || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.image_state === "real" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold">
                        <Check className="h-3 w-3" /> real
                      </span>
                    )}
                    {row.image_state === "placeholder" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-bold">
                        <AlertTriangle className="h-3 w-3" /> placeholder
                      </span>
                    )}
                    {row.image_state === "missing" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700 text-[11px] font-bold">
                        <X className="h-3 w-3" /> missing
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.has_any_pdf ? (
                      <span
                        title={
                          row.has_floor_plan_pdf ? "admin-uploaded PDF"
                          : row.plan_url_kind === "pdf" ? "seed PDF via plan_url"
                          : row.plan_files_kind === "pdf" ? `${row.plan_files_count}-floor PDF set`
                          : "PDF present"
                        }
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold"
                      >
                        <Check className="h-3 w-3" />
                        {row.has_floor_plan_pdf ? "admin"
                          : row.plan_files_kind === "pdf" ? `${row.plan_files_count}f`
                          : "pdf"}
                      </span>
                    ) : row.has_any_plan_asset ? (
                      <span
                        title="Image-only plan (JPG/PNG) — customer downloadable but not a PDF"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-bold"
                      >
                        img
                      </span>
                    ) : (
                      <span
                        title="No plan asset at all"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700 text-[11px] font-bold"
                      >
                        none
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.is_archived ? (
                      <span className="inline-block px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700 text-[11px] font-bold">
                        archived
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold">
                        active
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer note: reconciliation guarantee */}
      <div className="mt-3 text-[11px] text-muted-foreground leading-snug">
        Source: <code>GET /api/admin/library/health</code> · This count is
        read straight from <code>library_designs</code> with no archived
        filter applied — the same number appears in
        {" "}<code>GET /api/library</code>{" "}<code>?page_size=60</code> for the public store.
      </div>
    </section>
  );
};


const DEFAULT_FORM = {
  name: "",
  plot_width: "",
  plot_depth: "",
  num_floors: 1,
  facing: "any",
  bhk: 2,
  price: "",
  badge: "",
  is_bestseller: false,
  image_url: "",
  plan_url: "",
};

const LibraryManager = ({ onToast }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "add" | { mode: "edit", design }

  const reqId = useRef(0);
  const debounced = useDebouncedValue(search, 300);

  const fetchPage = useCallback(async () => {
    reqId.current += 1;
    const my = reqId.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debounced.trim()) params.set("q", debounced.trim());
      params.set("page", String(page));
      params.set("page_size", String(DB_PAGE_SIZE));
      const { data } = await axios.get(`${API}/library?${params.toString()}`);
      if (reqId.current !== my) return;
      setItems(data.items);
      setTotal(data.total);
    } catch {
      onToast("Failed to load designs", "error");
    } finally {
      reqId.current === my && setLoading(false);
    }
  }, [debounced, page, onToast]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Reset to page 1 whenever search query changes
  useEffect(() => {
    setPage(1);
  }, [debounced]);

  const totalPages = Math.max(1, Math.ceil(total / DB_PAGE_SIZE));

  const onCreated = () => {
    onToast("Design added");
    setModal(null);
    setPage(1);
    fetchPage();
  };
  const onUpdated = () => {
    onToast("Design updated");
    setModal(null);
    fetchPage();
  };
  const onDeleted = () => {
    onToast("Design deleted");
    fetchPage();
  };

  const remove = async (design) => {
    if (!window.confirm(`Delete "${design.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/library/${design.id}`);
      onDeleted();
    } catch {
      onToast("Delete failed", "error");
    }
  };

  return (
    <section className="mt-8" data-testid="admin-library">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">
            Design Library
          </div>
          <h2 className="mt-1 text-xl sm:text-2xl font-extrabold text-navy">
            Design Database
          </h2>
          <p className="text-sm text-muted-foreground">
            100+ ready designs — search, add, edit and delete in seconds.
          </p>
        </div>
        <button
          onClick={() => setModal("add")}
          data-testid="db-bulk-add-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gold text-navy font-extrabold text-sm shadow-[0_8px_18px_-8px_rgba(212,162,42,0.6)] hover:bg-gold-dark hover:text-white"
        >
          <Plus className="h-4 w-4" /> Bulk Add
        </button>
      </div>

      {/* Search + total */}
      <div className="flex items-center gap-3 flex-wrap mb-3" data-testid="db-toolbar">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 rounded-xl bg-white border-2 border-border px-3 py-2 focus-within:border-navy">
          <SearchIcon className="h-4 w-4 text-navy" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / size / badge"
            data-testid="db-search-input"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              aria-label="Clear"
              className="h-6 w-6 grid place-items-center rounded-full hover:bg-sand-alt"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>
        <div className="text-[12.5px] text-muted-foreground" data-testid="db-total">
          <b className="text-navy">{total}</b> designs
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="db-table-wrap">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="db-table">
            <thead className="bg-sand-alt">
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
                <th className="text-left py-2.5 px-3">Design</th>
                <th className="text-left py-2.5 px-3">Size</th>
                <th className="text-center py-2.5 px-3">BHK</th>
                <th className="text-left py-2.5 px-3">Facing</th>
                <th className="text-right py-2.5 px-3">Price</th>
                <th className="text-right py-2.5 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-muted-foreground" data-testid="db-loading">
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-muted-foreground italic" data-testid="db-empty">
                    No designs match.
                  </td>
                </tr>
              ) : (
                items.map((d) => (
                  <tr
                    key={d.id}
                    data-testid={`db-row-${d.id}`}
                    className="border-t border-border hover:bg-sand-alt/40"
                  >
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={d.image_url}
                          alt=""
                          loading="lazy"
                          className="h-10 w-14 object-cover rounded-md border border-border bg-sand-alt shrink-0"
                        />
                        <div className="min-w-0">
                          <div className="font-bold text-navy truncate flex items-center gap-1.5">
                            {d.is_bestseller && (
                              <span title="Bestseller" className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-gold text-navy text-[10px] font-extrabold shrink-0">
                                ★
                              </span>
                            )}
                            <span className="truncate">{d.name}</span>
                          </div>
                          {d.badge && (
                            <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded-full bg-gold/15 text-gold-dark border border-gold/30 text-[10px] font-bold">
                              {d.badge}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-mono font-bold text-navy">{d.size_label}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {d.area_sqft} sqft
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-center font-bold tabular-nums">{d.bhk}</td>
                    <td className="py-2.5 px-3 capitalize">{d.facing}</td>
                    <td className="py-2.5 px-3 text-right font-extrabold text-gold-dark tabular-nums">
                      {formatINR(d.price)}
                    </td>
                    <td className="py-2.5 px-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => setModal({ mode: "edit", design: d })}
                        data-testid={`db-edit-${d.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-navy text-gold text-[11.5px] font-bold hover:bg-navy-deep mr-1.5"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button
                        onClick={() => remove(d)}
                        data-testid={`db-delete-${d.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-200 text-[11.5px] font-bold hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 px-3 py-2.5 border-t border-border bg-sand-alt/40" data-testid="db-pagination">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="db-prev-page"
              className="px-3 py-1.5 rounded-lg border-2 border-border bg-white text-navy text-[12px] font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:border-navy/40"
            >
              ← Prev
            </button>
            <div className="text-[12px] text-muted-foreground">
              Page <b className="text-navy">{page}</b> of <b className="text-navy">{totalPages}</b>
            </div>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              data-testid="db-next-page"
              className="px-3 py-1.5 rounded-lg border-2 border-border bg-white text-navy text-[12px] font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:border-navy/40"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {modal === "add" && (
        <DesignFormModal
          mode="add"
          onClose={() => setModal(null)}
          onSuccess={onCreated}
          onToast={onToast}
        />
      )}
      {modal && modal.mode === "edit" && (
        <DesignFormModal
          mode="edit"
          design={modal.design}
          onClose={() => setModal(null)}
          onSuccess={onUpdated}
          onToast={onToast}
        />
      )}
    </section>
  );
};

const DesignFormModal = ({ mode, design, onClose, onSuccess, onToast }) => {
  const [form, setForm] = useState(() => {
    if (mode === "edit" && design) {
      return {
        name: design.name || "",
        plot_width: design.plot_width || "",
        plot_depth: design.plot_depth || "",
        num_floors: design.num_floors || 1,
        facing: design.facing || "any",
        bhk: design.bhk || 2,
        price: design.price || "",
        badge: design.badge || "",
        is_bestseller: !!design.is_bestseller,
        image_url: design.image_url || "",
        plan_url: design.plan_url || "",
      };
    }
    return DEFAULT_FORM;
  });
  // Track the drawing-file fields separately so DrawingFilesCard can
  // optimistically patch them without rerunning the parent form's
  // controlled inputs. Re-syncs whenever the modal opens on a new design.
  const [drawing, setDrawing] = useState(() => ({
    id: design?.id,
    floor_plan_pdf: design?.floor_plan_pdf || null,
    floor_plan_pdf_uploaded_at: design?.floor_plan_pdf_uploaded_at || null,
    technical_details_img: design?.technical_details_img || null,
    technical_details_img_uploaded_at: design?.technical_details_img_uploaded_at || null,
  }));
  useEffect(() => {
    setDrawing({
      id: design?.id,
      floor_plan_pdf: design?.floor_plan_pdf || null,
      floor_plan_pdf_uploaded_at: design?.floor_plan_pdf_uploaded_at || null,
      technical_details_img: design?.technical_details_img || null,
      technical_details_img_uploaded_at: design?.technical_details_img_uploaded_at || null,
    });
  }, [
    design?.id,
    design?.floor_plan_pdf,
    design?.floor_plan_pdf_uploaded_at,
    design?.technical_details_img,
    design?.technical_details_img_uploaded_at,
  ]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.plot_width || !form.plot_depth) {
      onToast("Name, plot width and depth are required", "error");
      return;
    }
    if (!form.image_url || !form.plan_url) {
      onToast("Both 3D view URL and 2D plan URL are required", "error");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      plot_width: Number(form.plot_width),
      plot_depth: Number(form.plot_depth),
      num_floors: Math.max(1, Number(form.num_floors) || 1),
      facing: form.facing,
      bhk: Number(form.bhk) || 2,
      price: Number(form.price) || 0,
      badge: form.badge ? form.badge.trim() : null,
      is_bestseller: !!form.is_bestseller,
      image_url: form.image_url.trim(),
      plan_url: form.plan_url.trim(),
    };
    try {
      if (mode === "add") {
        await axios.post(`${API}/library`, payload);
      } else {
        await axios.patch(`${API}/library/${design.id}`, payload);
      }
      onSuccess();
    } catch (err) {
      onToast(err?.response?.data?.detail || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const title = mode === "edit" ? "Edit design" : "Add new design";
  const sizePreview =
    form.plot_width && form.plot_depth
      ? `${form.plot_width}×${form.plot_depth} • ${
          Number(form.plot_width) * Number(form.plot_depth)
        } sqft`
      : "";

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[55] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="db-form-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white rounded-3xl border-2 border-gold shadow-2xl overflow-hidden animate-fade-up"
        data-testid="db-form-modal"
      >
        <div className="bg-navy text-white p-4 sm:p-5 relative">
          <button
            onClick={onClose}
            data-testid="db-form-close"
            className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="text-[11px] uppercase tracking-wider text-gold font-bold">
            Design Library
          </div>
          <h3 className="mt-1 text-xl font-extrabold">{title}</h3>
          {sizePreview && (
            <div className="mt-1 text-[12.5px] text-white/80 font-mono">{sizePreview}</div>
          )}
        </div>
        <form
          onSubmit={submit}
          className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto"
          data-testid="db-form"
        >
          <Field label="Design name *">
            <input
              type="text"
              value={form.name}
              onChange={set("name")}
              data-testid="db-input-name"
              placeholder="Adhunik Awas"
              className="lib-input"
            />
          </Field>
          <Field label="Badge (optional)">
            <input
              type="text"
              value={form.badge}
              onChange={set("badge")}
              data-testid="db-input-badge"
              placeholder="Modern / Premium"
              className="lib-input"
            />
          </Field>
          <label className="block sm:col-span-2">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
              Bestseller
            </span>
            <div className="mt-1 flex items-center gap-2 rounded-xl border-2 border-border bg-white px-3 py-2.5">
              <input
                type="checkbox"
                id="db-input-bestseller"
                checked={!!form.is_bestseller}
                onChange={(e) => setForm((f) => ({ ...f, is_bestseller: e.target.checked }))}
                data-testid="db-input-bestseller"
                className="h-4 w-4 accent-navy"
              />
              <label htmlFor="db-input-bestseller" className="text-sm font-semibold text-navy cursor-pointer">
                Mark as bestseller (shows ★ badge and pins to top of Design Library)
              </label>
            </div>
          </label>
          <Field label="Plot width (ft) *">
            <input
              type="number"
              value={form.plot_width}
              onChange={set("plot_width")}
              data-testid="db-input-width"
              placeholder="20"
              className="lib-input"
            />
          </Field>
          <Field label="Plot depth (ft) *">
            <input
              type="number"
              value={form.plot_depth}
              onChange={set("plot_depth")}
              data-testid="db-input-depth"
              placeholder="40"
              className="lib-input"
            />
          </Field>
          <Field label="Facing (N/S/E/W)">
            <select
              value={form.facing}
              onChange={set("facing")}
              data-testid="db-input-facing"
              className="lib-input"
            >
              <option value="any">Any</option>
              <option value="north">North</option>
              <option value="south">South</option>
              <option value="east">East</option>
              <option value="west">West</option>
            </select>
          </Field>
          <Field label="BHK">
            <select
              value={form.bhk}
              onChange={set("bhk")}
              data-testid="db-input-bhk"
              className="lib-input"
            >
              <option value={1}>1 BHK</option>
              <option value={2}>2 BHK</option>
              <option value={3}>3 BHK</option>
              <option value={4}>4 BHK</option>
            </select>
          </Field>
          <Field label="Floors (G+…)">
            <select
              value={form.num_floors}
              onChange={set("num_floors")}
              data-testid="db-input-num-floors"
              className="lib-input"
            >
              <option value={1}>1 floor (G only)</option>
              <option value={2}>2 floors (G+1)</option>
              <option value={3}>3 floors (G+2)</option>
              <option value={4}>4 floors (G+3)</option>
            </select>
          </Field>
          <Field label="Price (₹) *">
            <input
              type="number"
              value={form.price}
              onChange={set("price")}
              data-testid="db-input-price"
              placeholder="1499"
              className="lib-input"
            />
          </Field>
          <Field label="3D view URL *" wide>
            <input
              type="url"
              value={form.image_url}
              onChange={set("image_url")}
              data-testid="db-input-image"
              placeholder="https://..."
              className="lib-input"
            />
          </Field>
          <Field label="2D plan URL (PDF/JPG) *" wide>
            <input
              type="url"
              value={form.plan_url}
              onChange={set("plan_url")}
              data-testid="db-input-plan"
              placeholder="https://..."
              className="lib-input"
            />
          </Field>

          <div className="sm:col-span-2 flex justify-end gap-2 pt-3 border-t border-border mt-1">
            <button
              type="button"
              onClick={onClose}
              data-testid="db-form-cancel"
              className="min-h-[44px] px-4 rounded-xl border-2 border-border bg-white text-navy font-bold text-sm hover:bg-sand-alt"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="db-form-submit"
              className={`min-h-[44px] px-5 rounded-xl font-extrabold text-sm flex items-center gap-2 transition-all ${
                saving
                  ? "bg-sand-alt text-muted-foreground cursor-wait"
                  : "bg-gold text-navy hover:bg-gold-dark hover:text-white shadow-[0_8px_18px_-8px_rgba(212,162,42,0.6)]"
              }`}
            >
              {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Save design"}
              {!saving && <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </form>
        {/* Drawing files live OUTSIDE the form because the upload endpoint
            is a separate multipart call — the user shouldn't have to click
            "Save" to persist an upload. Only shown in edit mode (we need
            an existing design id to attach files to). */}
        {mode === "edit" && drawing.id && (
          <div className="mt-4">
            <DrawingFilesCard
              design={drawing}
              onPatch={(patch) => setDrawing((d) => ({ ...d, ...patch }))}
              onToast={onToast}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children, wide }) => (
  <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
    <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
      {label}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

const useDebouncedValue = (value, ms = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
};

// =============== Marketing Toolkit ===============

const SHARE_CARDS = [
  {
    id: "card-20x40",
    title: "20×40 Specialist",
    headline_hi: "20×40 के प्लॉट में शानदार घर!",
    sub_hi: "सिर्फ ₹999 में नक्शा डाउनलोड करें।",
    caption_hi:
      "20×40 के प्लॉट में शानदार घर! 🏗️\nसिर्फ ₹999 में नक्शा डाउनलोड करें।\nMera Naksha · meranaksha.com",
    badge: "Bestseller",
    price: "₹999",
    accent: "#D4A22A",
    designIdHints: ["lib-sukh-niwas", "lib-shanti-sadan", "lib-vasant-vihar"],
    sizeFilter: ["20x40", "20x35", "18x34", "20x43"],
  },
  {
    id: "card-30x50",
    title: "30×50 Premium",
    headline_hi: "बड़े सपनों के लिए बड़ा घर!",
    sub_hi: "30×50 का प्रीमियम डिज़ाइन ₹1,999 में।",
    caption_hi:
      "बड़े सपनों के लिए बड़ा घर! 🏡\n30×50 का प्रीमियम डिज़ाइन — सिर्फ ₹1,999 में।\nMera Naksha · meranaksha.com",
    badge: "Premium",
    price: "₹1,999",
    accent: "#D4A22A",
    designIdHints: ["lib-rajwada-bungalow", "lib-aaradhya-villa", "lib-gauri-villa"],
    sizeFilter: ["30x50", "30x60", "35x50"],
  },
  {
    id: "card-brand",
    title: "General Branding",
    headline_hi: "अब गाँव में भी बनेगा",
    sub_hi: "शहर जैसा शानदार घर।",
    caption_hi:
      "Mera Naksha: अब गाँव में भी बनेगा शहर जैसा शानदार घर। 🚩\n27+ रेडीमेड नक्शे · ₹999 से शुरू · इंस्टेंट डाउनलोड\nmeranaksha.com",
    badge: "Mera Naksha",
    price: null,
    accent: "#D4A22A",
    designIdHints: ["lib-vyaapaar-niwas", "lib-pushpa-vihar", "lib-swarna-sadan"],
    sizeFilter: null, // any
  },
];

const COMMUNITY_URL = "https://chat.whatsapp.com/IsXqA6Z7hqSGmbvSjL30LL";

// Canvas helper: draw a rounded rectangle path. ctx.fill()/ctx.stroke() decides the rendering.
const roundRect = (ctx, x, y, w, h, r) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const drawShareCard = async (canvas, card, design) => {
  const ctx = canvas.getContext("2d");
  const W = 1080;
  const H = 1080;
  canvas.width = W;
  canvas.height = H;

  // Background — Navy with subtle radial highlight
  const grad = ctx.createRadialGradient(W * 0.85, H * 0.15, 50, W * 0.5, H * 0.5, W);
  grad.addColorStop(0, "#163B6E");
  grad.addColorStop(1, "#0B2545");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Top gold ribbon
  ctx.fillStyle = card.accent;
  ctx.fillRect(0, 0, W, 12);

  // Brand logo (top-left)
  ctx.fillStyle = card.accent;
  ctx.font = "700 36px Inter, system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("मेरा नक्शा · Mera Naksha", 60, 50);

  // Badge (top-right)
  if (card.badge) {
    ctx.font = "700 20px Inter, system-ui, sans-serif";
    const bw = ctx.measureText(card.badge).width + 36;
    const bx = W - 60 - bw;
    const by = 48;
    ctx.fillStyle = card.accent;
    roundRect(ctx, bx, by, bw, 36, 18);
    ctx.fill();
    ctx.fillStyle = "#0B2545";
    ctx.textAlign = "center";
    ctx.fillText(card.badge, bx + bw / 2, by + 8);
  }

  // Hero image (only if a design was selected)
  let imageBottom = 320;
  if (design && design.image_url && !design.image_url.startsWith("/api/")) {
    const img = await loadCrossOriginImage(design.image_url).catch(() => null);
    if (img) {
      // Card frame
      const margin = 60;
      const ix = margin;
      const iy = 130;
      const iw = W - margin * 2;
      const ih = 540;
      ctx.save();
      roundRect(ctx, ix, iy, iw, ih, 32);
      ctx.clip();
      // Cover-fit
      const ratio = Math.max(iw / img.width, ih / img.height);
      const dw = img.width * ratio;
      const dh = img.height * ratio;
      const dx = ix + (iw - dw) / 2;
      const dy = iy + (ih - dh) / 2;
      ctx.drawImage(img, dx, dy, dw, dh);
      ctx.restore();
      // Border
      ctx.strokeStyle = "rgba(212,162,42,0.5)";
      ctx.lineWidth = 4;
      roundRect(ctx, ix, iy, iw, ih, 32);
      ctx.stroke();
      imageBottom = iy + ih + 40;

      // Size pill on image
      if (design.size_label) {
        ctx.font = "800 26px Inter, system-ui, sans-serif";
        const sizeText = design.size_label;
        const sw = ctx.measureText(sizeText).width + 40;
        ctx.fillStyle = "rgba(11,37,69,0.92)";
        roundRect(ctx, ix + 20, iy + 20, sw, 48, 24);
        ctx.fill();
        ctx.fillStyle = card.accent;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(sizeText, ix + 20 + sw / 2, iy + 44);
      }
    }
  }

  // Headline (Hindi)
  ctx.fillStyle = "#FFFFFF";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.font = "800 64px Inter, system-ui, sans-serif";
  wrapText(ctx, card.headline_hi, 60, imageBottom, W - 120, 78);

  // Subline
  const subY = imageBottom + 170;
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "500 36px Inter, system-ui, sans-serif";
  wrapText(ctx, card.sub_hi, 60, subY, W - 120, 50);

  // Price pill (bottom-left)
  if (card.price) {
    const py = H - 180;
    const ptext = card.price;
    ctx.font = "900 56px Inter, system-ui, sans-serif";
    const pw = ctx.measureText(ptext).width + 56;
    ctx.fillStyle = card.accent;
    roundRect(ctx, 60, py, pw, 84, 42);
    ctx.fill();
    ctx.fillStyle = "#0B2545";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(ptext, 60 + pw / 2, py + 42);
  }

  // Footer URL (bottom-right)
  ctx.font = "700 28px Inter, system-ui, sans-serif";
  ctx.fillStyle = card.accent;
  ctx.textAlign = "right";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("meranaksha.com", W - 60, H - 60);

  // Footer line above url
  ctx.font = "500 22px Inter, system-ui, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.fillText("WhatsApp: +91 97176 60160", W - 60, H - 100);
};

const loadCrossOriginImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
  const words = text.split(/\s+/);
  let line = "";
  let cy = y;
  for (let i = 0; i < words.length; i++) {
    const testLine = line ? line + " " + words[i] : words[i];
    if (ctx.measureText(testLine).width > maxWidth && line) {
      ctx.fillText(line, x, cy);
      line = words[i];
      cy += lineHeight;
    } else {
      line = testLine;
    }
  }
  if (line) ctx.fillText(line, x, cy);
};

// ----- System Health & Reliability widget -----
// Polls /api/admin/system-health every 20s and surfaces 4 pillars:
//   • Webhook Recovery Monitor   • Payment Reliability Feed
//   • Internet Latency Guard     • Security Pulse
const _outcomeChip = {
  success: { bg: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Success" },
  failed: { bg: "bg-red-100 text-red-800 border-red-300", label: "Failed" },
  cancelled: { bg: "bg-amber-100 text-amber-800 border-amber-300", label: "Cancelled" },
  suspicious: { bg: "bg-purple-100 text-purple-800 border-purple-300", label: "Suspicious" },
  timeout: { bg: "bg-orange-100 text-orange-800 border-orange-300", label: "Timeout" },
};

const SystemHealthWidget = () => {
  const [h, setH] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancel = false;
    const load = () => {
      axios
        .get(`${API}/admin/system-health`)
        .then(({ data }) => {
          if (!cancel) {
            setH(data);
            setErr("");
          }
        })
        .catch(() => {
          if (!cancel) setErr("Failed to load system health");
        });
    };
    load();
    const tick = setInterval(load, 20_000);
    return () => {
      cancel = true;
      clearInterval(tick);
    };
  }, []);

  if (!h && !err) {
    return (
      <section
        className="mb-8 rounded-3xl bg-white border border-border p-6 text-center text-muted-foreground"
        data-testid="admin-system-health-loading"
      >
        Loading System Health…
      </section>
    );
  }

  const totalOutcomes = (h?.success_count || 0) + (h?.failed_count || 0) + (h?.suspicious_count || 0);
  const successPct = totalOutcomes
    ? Math.round(((h?.success_count || 0) / totalOutcomes) * 100)
    : 100;
  const latencyOk = !h?.avg_page_load_ms || h.avg_page_load_ms < 2500;
  const isTest = h?.payment_mode_detected === "test";
  const isLive = h?.payment_mode_detected === "live";
  const razorpayHint = (() => {
    if (!h?.razorpay_configured) return "Setup Required — add RAZORPAY_KEY_ID/SECRET to .env";
    if (h?.payment_mode_mismatch)
      return `Mismatch — selected ${h?.payment_mode_configured?.toUpperCase()} but key is ${h?.payment_mode_detected?.toUpperCase()}`;
    if (isTest) return "Test keys detected · sandbox mode";
    if (isLive) return "Live keys detected · real customer payments";
    return "Configured";
  })();
  const keysIsolatedHint = (() => {
    if (h?.keys_source === "environment")
      return "Loaded from .env / Kubernetes secret · never exposed to frontend";
    if (h?.keys_source === "database")
      return "Stored encrypted in DB · never exposed to frontend";
    return "Setup required — no keys configured yet";
  })();
  const keysIsolatedOk = !!h?.keys_isolated && h?.keys_source !== "none";
  const securityChecks = [
    {
      label: "HTTPS Active",
      icon: Lock,
      ok: !!h?.https_active,
      hint: h?.https_active ? "Live HTTPS detected" : "Running on HTTP (preview only)",
    },
    {
      label: "API Keys Isolated",
      icon: KeyRound,
      ok: keysIsolatedOk,
      okLabel: keysIsolatedOk ? "Fully Secured" : "Setup required",
      hint: keysIsolatedHint,
    },
    {
      label: "Razorpay Configured",
      icon: CreditCard,
      ok: !!h?.razorpay_configured && !h?.payment_mode_mismatch,
      okLabel: h?.payment_mode_mismatch ? "Mismatch" : (isLive ? "Live mode" : isTest ? "Test mode" : "OK"),
      hint: razorpayHint,
    },
    {
      label: "Webhook Secret",
      icon: Webhook,
      ok: !!h?.webhook_secret_set,
      hint: h?.webhook_secret_set ? "Signature verification enabled" : "Add RAZORPAY_WEBHOOK_SECRET to .env to verify webhooks",
    },
  ];

  return (
    <section
      className="mb-8 rounded-3xl bg-gradient-to-br from-navy via-navy-deep to-navy text-white p-5 sm:p-6 shadow-[0_24px_48px_-24px_rgba(11,37,69,0.45)] border-2 border-gold/40 relative overflow-hidden"
      data-testid="admin-system-health"
    >
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
      <header className="flex items-center gap-3 mb-5">
        <div className="h-12 w-12 rounded-2xl bg-gold text-navy grid place-items-center shrink-0 shadow-md">
          <Activity className="h-6 w-6" strokeWidth={2.4} />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold font-bold">
            Live · auto-refresh 20s
          </div>
          <h2 className="text-xl font-extrabold tracking-tight">System Health & Reliability</h2>
        </div>
        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-[11.5px] font-bold">
          <span className={`h-2 w-2 rounded-full ${err ? "bg-red-400" : "bg-emerald-400 animate-pulse"}`} />
          {err ? "Degraded" : "All systems operational"}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 1. Webhook Recovery Monitor */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="health-webhook-recovery">
          <div className="flex items-center gap-2 text-gold text-[11px] uppercase tracking-wider font-bold">
            <Webhook className="h-3.5 w-3.5" /> Webhook Recovery Monitor
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <div className="text-4xl font-extrabold tabular-nums text-white">
              {h?.webhook_recoveries ?? 0}
            </div>
            <div className="text-[12.5px] text-white/70">
              orders unlocked in background after the customer closed the tab
            </div>
          </div>
          <div className="mt-2 text-[11.5px] text-white/55">
            Total verified payments: <span className="font-bold text-white">{h?.success_count ?? 0}</span>
            {" · "}
            Recovery rate:{" "}
            <span className="font-bold text-gold">
              {h?.success_count ? Math.round(((h?.webhook_recoveries || 0) / h.success_count) * 100) : 0}%
            </span>
          </div>
        </div>

        {/* 3. Latency Guard */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="health-latency">
          <div className="flex items-center gap-2 text-gold text-[11px] uppercase tracking-wider font-bold">
            <Wifi className="h-3.5 w-3.5" /> Internet Latency Guard
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <div className={`text-4xl font-extrabold tabular-nums ${latencyOk ? "text-white" : "text-amber-300"}`}>
              {h?.avg_page_load_ms ? `${h.avg_page_load_ms}ms` : "—"}
            </div>
            <div className="text-[12.5px] text-white/70">
              avg. page-load (last {h?.latency_samples ?? 0} samples)
            </div>
          </div>
          <div className="mt-2 text-[11.5px] text-white/55">
            {latencyOk
              ? "Healthy for Tier-3/4 mobile networks (target <2.5s)."
              : "Above 2.5s — consider lighter image variants for slow connections."}
          </div>
        </div>

        {/* 2. Reliability Feed (full-width) */}
        <div className="lg:col-span-2 rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="health-feed">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-gold text-[11px] uppercase tracking-wider font-bold">
              <Activity className="h-3.5 w-3.5" /> Payment Reliability Feed
            </div>
            <div className="text-[11px] text-white/70">
              Success rate:{" "}
              <span className={`font-extrabold ${successPct >= 95 ? "text-emerald-300" : successPct >= 80 ? "text-amber-300" : "text-red-300"}`}>
                {successPct}%
              </span>
              {" · "}
              {totalOutcomes} attempts logged
            </div>
          </div>
          <div className="mt-3 max-h-56 overflow-y-auto pr-1 space-y-1.5">
            {(!h?.recent_attempts || h.recent_attempts.length === 0) ? (
              <div className="text-[12.5px] text-white/55 italic py-3 text-center">
                No payment attempts yet — waiting for the first transaction.
              </div>
            ) : (
              h.recent_attempts.map((a) => {
                const chip = _outcomeChip[a.outcome] || _outcomeChip.failed;
                const t = new Date(a.created_at);
                const tStr = `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
                return (
                  <div
                    key={a.id}
                    data-testid={`health-feed-row-${a.outcome}`}
                    className="flex items-center gap-2 text-[12.5px] py-1.5 px-2 rounded-md bg-white/5 hover:bg-white/10"
                  >
                    <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-extrabold uppercase tracking-wider border ${chip.bg}`}>
                      {chip.label}
                    </span>
                    <span className="font-mono text-white/80">{tStr}</span>
                    <span className="text-white/85 truncate flex-1">
                      {a.receipt_no || "—"} · {a.design_name || "—"} · ₹{a.amount || 0}
                    </span>
                    <span className="text-white/50 text-[11px] hidden sm:inline">{a.detail}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* 4. Security Pulse */}
        <div className="lg:col-span-2 rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="health-security">
          <div className="flex items-center gap-2 text-gold text-[11px] uppercase tracking-wider font-bold">
            <ShieldCheck className="h-3.5 w-3.5" /> Security Pulse
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {securityChecks.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  data-testid={`security-check-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`rounded-xl p-3 border-2 ${
                    s.ok
                      ? "bg-emerald-500/15 border-emerald-400/40"
                      : "bg-red-500/15 border-red-400/40"
                  }`}
                >
                  <div className="flex items-center gap-1.5 text-white">
                    <Icon className={`h-3.5 w-3.5 ${s.ok ? "text-emerald-300" : "text-red-300"}`} />
                    <span className="font-extrabold text-[12px]">{s.label}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-1.5">
                    {s.ok ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
                    ) : (
                      <AlertTriangle className="h-3.5 w-3.5 text-red-300" />
                    )}
                    <span className={`text-[11px] font-bold ${s.ok ? "text-emerald-200" : "text-red-200"}`}>
                      {s.okLabel || (s.ok ? "OK" : "Setup required")}
                    </span>
                  </div>
                  <div className="mt-1 text-[10.5px] text-white/55 leading-snug">
                    {s.hint}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};


// ----- Payment & Pricing Controls -----
// Datetime-local <input> values look like "2026-05-07T18:30". The backend
// expects ISO 8601 (with timezone). These helpers do a faithful round-trip
// using the user's local timezone so the bound saved is what the admin sees.
const _dtLocalToISO = (v) => {
  if (!v) return null;
  // Treat as local time → convert to UTC ISO
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};
const _isoToDtLocal = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  // Build a local "YYYY-MM-DDTHH:mm" string
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

// ----- Secure Payment Settings (Razorpay credentials manager) -----
// Lets the founder plug live Razorpay keys + flip Test/Live mode without
// editing .env. The Key Secret is write-only — the GET endpoint never
// returns it. Once valid keys are saved, the platform automatically
// switches from Manual UPI fallback to the Razorpay Automated checkout.
const SecurePaymentSettingsPanel = ({ onToast }) => {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({
    key_id: "",
    key_secret: "",
    webhook_secret: "",
    mode: "live",
  });
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const refresh = useCallback(() => {
    setLoadError(null);
    adminGet("/admin/razorpay-credentials").then((res) => {
      if (res.ok) {
        const data = res.data || {};
        setMeta(data);
        setForm((f) => ({
          ...f,
          key_id: data.key_id || "",
          mode: data.mode || "live",
          key_secret: "",
          webhook_secret: "",
        }));
      } else {
        setLoadError({ status: res.status, message: res.error });
        onToast?.(res.error || "Failed to load Razorpay credentials", "error");
      }
      setLoaded(true);
    });
  }, [onToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.key_id.trim()) {
      onToast("Razorpay Key ID is required", "error");
      return;
    }
    if (form.mode === "live" && !form.key_id.startsWith("rzp_live_")) {
      onToast("Live mode keys must start with rzp_live_", "error");
      return;
    }
    if (form.mode === "test" && !form.key_id.startsWith("rzp_test_")) {
      onToast("Test mode keys must start with rzp_test_", "error");
      return;
    }
    setSaving(true);
    try {
      // Build a partial update — empty secret fields are omitted so we
      // don't blank out an existing saved secret.
      const body = {
        key_id: form.key_id.trim(),
        mode: form.mode,
      };
      if (form.key_secret.trim()) body.key_secret = form.key_secret.trim();
      if (form.webhook_secret.trim()) body.webhook_secret = form.webhook_secret.trim();
      const { data } = await axios.put(`${API}/admin/razorpay-credentials`, body);
      setMeta(data);
      setForm((f) => ({ ...f, key_secret: "", webhook_secret: "" }));
      setShowSecret(false);
      setShowWebhook(false);
      onToast(
        data.razorpay_configured
          ? "✓ Razorpay credentials saved · Automated payments active"
          : "Saved",
      );
    } catch (e) {
      onToast(e?.response?.data?.detail || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <section
        className="mb-8 rounded-2xl bg-white border border-border p-6 text-center text-muted-foreground"
        data-testid="admin-secure-payments-loading"
      >
        Loading Secure Payment Settings…
      </section>
    );
  }

  if (loadError && !meta) {
    return (
      <section
        className="mb-8 rounded-3xl bg-amber-50 border-2 border-amber-300 p-6"
        data-testid="admin-secure-payments-error"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-extrabold text-amber-900">
              Secure Payment Settings failed to load
            </h3>
            <p className="text-[13px] text-amber-900/90 mt-1">
              {loadError.message}{" "}
              {loadError.status ? <span className="font-mono text-[11px]">(HTTP {loadError.status})</span> : null}
            </p>
            <button
              onClick={refresh}
              data-testid="admin-secure-payments-retry"
              className="mt-3 min-h-[40px] px-3 rounded-xl bg-navy text-gold font-extrabold text-[12.5px] inline-flex items-center gap-1.5 hover:bg-navy-deep"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  const ready = meta?.razorpay_configured;
  const detectedMode = meta?.mode_detected || "none"; // "test" | "live" | "none"
  const configuredMode = meta?.mode || form.mode;
  const liveMode = configuredMode === "live";
  // Mismatch = user has picked one mode in the toggle, but the saved key
  // belongs to the other mode. Surface this prominently — it's the most
  // common foot-gun (e.g. accidentally pasting test keys into live mode).
  const formMismatch = (() => {
    if (!form.key_id) return false;
    if (form.mode === "live" && !form.key_id.startsWith("rzp_live_")) return true;
    if (form.mode === "test" && !form.key_id.startsWith("rzp_test_")) return true;
    return false;
  })();
  const savedMismatch = !!meta && meta.mode_detected !== "none" && meta.mode_detected !== meta.mode;

  return (
    <section
      className="mb-8 rounded-3xl bg-white border-2 border-navy/15 shadow-[0_24px_48px_-24px_rgba(11,37,69,0.25)] overflow-hidden"
      data-testid="admin-secure-payments"
    >
      <header className="bg-gradient-to-r from-navy via-navy-deep to-navy text-white p-5 flex items-center gap-3 relative">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
        <div className="h-12 w-12 rounded-2xl bg-gold text-navy grid place-items-center shrink-0 shadow-md">
          <Lock className="h-6 w-6" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold font-bold">
            Storefront · Razorpay
          </div>
          <h2 className="text-xl font-extrabold tracking-tight">Secure Payment Settings</h2>
        </div>
        {ready ? (
          <div
            data-testid={`payments-mode-badge-${detectedMode}`}
            className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-extrabold tracking-wider uppercase shadow-md ${
              detectedMode === "live"
                ? "bg-emerald-500 text-white"
                : "bg-amber-400 text-navy"
            }`}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            {detectedMode === "live"
              ? "Payment Mode · LIVE MODE"
              : "Payment Mode · TEST MODE"}
          </div>
        ) : (
          <div
            data-testid="payments-setup-required-badge"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400 text-navy text-[11.5px] font-extrabold tracking-wider uppercase shadow-md"
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Setup Required
          </div>
        )}
      </header>

      <div className="p-5 sm:p-6 space-y-5">
        {/* Status row (mobile) */}
        <div className="sm:hidden">
          {ready ? (
            <div
              data-testid={`payments-mode-badge-mobile-${detectedMode}`}
              className={`rounded-xl px-3 py-2 text-[12.5px] font-extrabold flex items-center gap-2 ${
                detectedMode === "live"
                  ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                  : "bg-amber-50 border border-amber-200 text-amber-800"
              }`}
            >
              <CheckCircle2 className="h-4 w-4" />
              {detectedMode === "live"
                ? "Payment Mode · LIVE MODE — real customer payments"
                : "Payment Mode · TEST MODE — sandbox keys, no real money"}
            </div>
          ) : (
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[12.5px] font-extrabold text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Setup Required — add your Razorpay keys below
            </div>
          )}
        </div>

        {/* Mode mismatch (saved state) — blocks production traffic if true */}
        {savedMismatch && (
          <div
            data-testid="payments-mismatch-banner-saved"
            className="rounded-2xl bg-red-50 border-2 border-red-300 px-4 py-3 text-[13px] flex items-start gap-3"
          >
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-red-700">
                Configuration conflict detected
              </div>
              <div className="text-red-700/90 mt-0.5 leading-snug">
                Selected mode is{" "}
                <strong className="uppercase">{meta?.mode}</strong> but the
                stored Key ID is a{" "}
                <strong className="uppercase">{meta?.mode_detected}</strong>{" "}
                key. Update the Key ID or switch the mode below — saves are
                blocked until the two agree.
              </div>
            </div>
          </div>
        )}

        {/* Mode toggle */}
        <div className="rounded-2xl border-2 border-border p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-extrabold text-navy">Payment Mode</div>
              <div className="text-[12px] text-muted-foreground">
                Test mode uses sandbox keys (`rzp_test_…`). Live mode charges real customers.
              </div>
            </div>
            <div
              role="tablist"
              aria-label="Payment mode"
              className="inline-flex bg-sand-alt rounded-full p-1"
              data-testid="admin-payment-mode-toggle"
            >
              {["test", "live"].map((m) => (
                <button
                  key={m}
                  onClick={() => set("mode", m)}
                  data-testid={`admin-payment-mode-${m}`}
                  role="tab"
                  aria-selected={form.mode === m}
                  className={`px-4 py-1.5 rounded-full text-[12px] font-extrabold uppercase tracking-wider transition-all ${
                    form.mode === m
                      ? m === "live"
                        ? "bg-emerald-500 text-white shadow"
                        : "bg-amber-400 text-navy shadow"
                      : "text-muted-foreground hover:text-navy"
                  }`}
                >
                  {m === "live" ? "Live Mode" : "Test Mode"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Key ID */}
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
            Razorpay Key ID
          </label>
          <input
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={form.key_id}
            onChange={(e) => set("key_id", e.target.value)}
            placeholder={liveMode ? "rzp_live_xxxxxxxxxxxxxx" : "rzp_test_xxxxxxxxxxxxxx"}
            data-testid="admin-razorpay-key-id"
            className="mt-1 w-full rounded-xl border-2 border-border bg-white px-3 py-2.5 text-sm font-mono focus:border-navy focus:outline-none"
          />
          {meta?.key_id_masked && !form.key_id && (
            <div className="mt-1 text-[11px] text-muted-foreground">
              Currently saved: <span className="font-mono font-bold">{meta.key_id_masked}</span>
            </div>
          )}
        </div>

        {/* Key Secret (write-only) */}
        <div>
          <div className="flex items-center justify-between">
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
              Razorpay Key Secret
            </label>
            <span
              className={`text-[10.5px] font-extrabold tracking-wider uppercase px-2 py-0.5 rounded-md ${
                meta?.has_key_secret
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-700"
              }`}
              data-testid="admin-key-secret-status"
            >
              {meta?.has_key_secret ? "✓ Saved" : "Not set"}
            </span>
          </div>
          <div className="mt-1 relative">
            <input
              type={showSecret ? "text" : "password"}
              autoComplete="new-password"
              spellCheck={false}
              value={form.key_secret}
              onChange={(e) => set("key_secret", e.target.value)}
              placeholder={meta?.has_key_secret ? "•••••••••• (leave blank to keep current)" : "Paste secret here"}
              data-testid="admin-razorpay-key-secret"
              className="w-full rounded-xl border-2 border-border bg-white px-3 py-2.5 pr-11 text-sm font-mono focus:border-navy focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setShowSecret((s) => !s)}
              data-testid="admin-toggle-secret-visibility"
              aria-label={showSecret ? "Hide secret" : "Show secret"}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:text-navy hover:bg-sand-alt"
            >
              {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground flex items-start gap-1.5">
            <Lock className="h-3 w-3 mt-0.5 shrink-0" />
            Stored encrypted on the backend. Never returned in API responses or sent to the frontend after save.
          </div>
        </div>

        {/* Webhook Secret (optional) */}
        <div>
          <div className="flex items-center justify-between">
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
              Webhook Secret <span className="font-normal lowercase tracking-normal text-muted-foreground/70">(optional, recommended)</span>
            </label>
            <span
              className={`text-[10.5px] font-extrabold tracking-wider uppercase px-2 py-0.5 rounded-md ${
                meta?.has_webhook_secret
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-sand-alt text-muted-foreground"
              }`}
              data-testid="admin-webhook-secret-status"
            >
              {meta?.has_webhook_secret ? "✓ Saved" : "Not set"}
            </span>
          </div>
          <div className="mt-1 relative">
            <input
              type={showWebhook ? "text" : "password"}
              autoComplete="new-password"
              spellCheck={false}
              value={form.webhook_secret}
              onChange={(e) => set("webhook_secret", e.target.value)}
              placeholder={meta?.has_webhook_secret ? "•••••••••• (leave blank to keep current)" : "From Razorpay → Webhooks"}
              data-testid="admin-razorpay-webhook-secret"
              className="w-full rounded-xl border-2 border-border bg-white px-3 py-2.5 pr-11 text-sm font-mono focus:border-navy focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setShowWebhook((s) => !s)}
              data-testid="admin-toggle-webhook-visibility"
              aria-label={showWebhook ? "Hide secret" : "Show secret"}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:text-navy hover:bg-sand-alt"
            >
              {showWebhook ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            Configure your Razorpay Dashboard webhook URL to{" "}
            <code className="font-mono font-bold text-navy">
              {(process.env.REACT_APP_BACKEND_URL || "") + "/api/razorpay/webhook"}
            </code>
            {" "}— this lets us auto-unlock plans even if the customer closes the tab.
          </div>
        </div>

        {/* Form-level mismatch (live edits in the panel) */}
        {formMismatch && (
          <div
            data-testid="payments-mismatch-banner-form"
            className="rounded-2xl bg-amber-50 border-2 border-amber-300 px-4 py-3 text-[13px] flex items-start gap-3"
          >
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-amber-800">
                {form.mode === "live"
                  ? "Live Mode selected but Key ID is a TEST key"
                  : "Test Mode selected but Key ID is a LIVE key"}
              </div>
              <div className="text-amber-800/90 mt-0.5 leading-snug">
                Live mode requires <code className="font-mono">rzp_live_*</code>,
                Test mode requires <code className="font-mono">rzp_test_*</code>.
                Saves are blocked until they match.
              </div>
            </div>
          </div>
        )}

        {/* Save row */}
        <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
          <div className="text-[11.5px] text-muted-foreground">
            {meta?.updated_at ? (
              <>
                Last updated:{" "}
                <span className="font-bold text-navy">{new Date(meta.updated_at).toLocaleString()}</span>
              </>
            ) : (
              <>Source:{" "}<span className="font-bold">{meta?.source || "none"}</span></>
            )}
          </div>
          <button
            onClick={save}
            disabled={saving || formMismatch}
            data-testid="admin-secure-payments-save"
            className={`min-h-[48px] px-5 rounded-xl font-extrabold text-[13.5px] flex items-center gap-2 transition-all ${
              saving || formMismatch
                ? "bg-gold/40 text-navy/60 cursor-not-allowed"
                : "bg-navy text-gold hover:bg-navy-deep"
            }`}
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving & verifying…" : formMismatch ? "Fix mismatch to save" : "Save & Activate"}
          </button>
        </div>
      </div>
    </section>
  );
};


const PaymentPricingPanel = ({ onToast }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    adminGet("/admin/payment-settings").then((res) => {
      if (cancelled) return;
      if (res.ok) {
        const data = res.data || {};
        setForm({
          payments_enabled: !!data.payments_enabled,
          payments_disabled_message: data.payments_disabled_message || "",
          discount_percent: data.discount_percent || 0,
          discount_start_local: _isoToDtLocal(data.discount_start),
          discount_end_local: _isoToDtLocal(data.discount_end),
          banner_enabled: !!data.banner_enabled,
          banner_message_en: data.banner_message_en || "",
          banner_message_hi: data.banner_message_hi || "",
          bank_transfer_enabled: !!data.bank_transfer_enabled,
          bank_account_holder: data.bank_account_holder || "",
          bank_account_number: data.bank_account_number || "",
          bank_ifsc: data.bank_ifsc || "",
          bank_name: data.bank_name || "",
          bank_notes: data.bank_notes || "",
        });
      } else {
        setLoadError({ status: res.status, message: res.error });
        onToast?.(res.error || "Failed to load payment settings", "error");
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [onToast]);

  useEffect(() => {
    const cancel = load();
    return cancel;
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        payments_enabled: form.payments_enabled,
        payments_disabled_message: form.payments_disabled_message.trim(),
        discount_percent: Math.max(0, Math.min(100, Number(form.discount_percent) || 0)),
        discount_start: _dtLocalToISO(form.discount_start_local),
        discount_end: _dtLocalToISO(form.discount_end_local),
        banner_enabled: form.banner_enabled,
        banner_message_en: form.banner_message_en.trim(),
        banner_message_hi: form.banner_message_hi.trim(),
        bank_transfer_enabled: !!form.bank_transfer_enabled,
        bank_account_holder: (form.bank_account_holder || "").trim(),
        bank_account_number: (form.bank_account_number || "").trim(),
        bank_ifsc: (form.bank_ifsc || "").trim().toUpperCase(),
        bank_name: (form.bank_name || "").trim(),
        bank_notes: (form.bank_notes || "").trim(),
      };
      const { data } = await axios.put(`${API}/admin/payment-settings`, payload);
      setForm({
        payments_enabled: !!data.payments_enabled,
        payments_disabled_message: data.payments_disabled_message || "",
        discount_percent: data.discount_percent || 0,
        discount_start_local: _isoToDtLocal(data.discount_start),
        discount_end_local: _isoToDtLocal(data.discount_end),
        banner_enabled: !!data.banner_enabled,
        banner_message_en: data.banner_message_en || "",
        banner_message_hi: data.banner_message_hi || "",
        bank_transfer_enabled: !!data.bank_transfer_enabled,
        bank_account_holder: data.bank_account_holder || "",
        bank_account_number: data.bank_account_number || "",
        bank_ifsc: data.bank_ifsc || "",
        bank_name: data.bank_name || "",
        bank_notes: data.bank_notes || "",
      });
      onToast("Payment & pricing settings saved");
    } catch (e) {
      onToast(e?.response?.data?.detail || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loadError && !form) {
    return (
      <section
        className="mb-8 rounded-3xl bg-amber-50 border-2 border-amber-300 p-6"
        data-testid="admin-payment-error"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-extrabold text-amber-900">
              Payment &amp; pricing controls failed to load
            </h3>
            <p className="text-[13px] text-amber-900/90 mt-1">
              {loadError.message}{" "}
              {loadError.status ? <span className="font-mono text-[11px]">(HTTP {loadError.status})</span> : null}
            </p>
            <button
              onClick={load}
              data-testid="admin-payment-retry"
              className="mt-3 min-h-[40px] px-3 rounded-xl bg-navy text-gold font-extrabold text-[12.5px] inline-flex items-center gap-1.5 hover:bg-navy-deep"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (loading || !form) {
    return (
      <section
        className="mb-8 rounded-2xl bg-white border border-border p-6 text-center text-muted-foreground"
        data-testid="admin-payment-loading"
      >
        Loading payment & pricing controls…
      </section>
    );
  }

  // Live preview: figure out if discount window is currently active
  const now = new Date();
  const start = form.discount_start_local ? new Date(form.discount_start_local) : null;
  const end = form.discount_end_local ? new Date(form.discount_end_local) : null;
  const inWindow = (!start || now >= start) && (!end || now <= end);
  const previewActive = form.discount_percent > 0 && inWindow;
  const previewBannerVisible =
    form.banner_enabled && (previewActive || !form.payments_enabled);

  return (
    <section
      className="mb-8 rounded-3xl bg-gradient-to-br from-white via-white to-sand-alt/40 border-2 border-gold/40 shadow-[0_24px_48px_-24px_rgba(11,37,69,0.25)] overflow-hidden"
      data-testid="admin-payment-panel"
    >
      <header className="bg-navy text-white p-5 flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-gold text-navy grid place-items-center shrink-0 shadow-md">
          <CreditCard className="h-6 w-6" strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold font-bold">
            Storefront
          </div>
          <h2 className="text-xl font-extrabold tracking-tight">
            Payment & Pricing Controls
          </h2>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="admin-payment-save-btn"
          className={`hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-extrabold text-[13px] transition-all ${
            saving
              ? "bg-gold/40 text-navy/60 cursor-not-allowed"
              : "bg-gold text-navy hover:bg-gold-dark hover:text-white"
          }`}
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving…" : "Save changes"}
        </button>
      </header>

      <div className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* 1. Master Payment Toggle */}
        <div className="rounded-2xl border-2 border-border p-4">
          <div className="flex items-start gap-3">
            <div
              className={`h-10 w-10 rounded-xl grid place-items-center shrink-0 ${
                form.payments_enabled
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {form.payments_enabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <div className="font-extrabold text-navy">Master Payment Toggle</div>
                <button
                  onClick={() => set("payments_enabled", !form.payments_enabled)}
                  data-testid="admin-payments-toggle"
                  role="switch"
                  aria-checked={form.payments_enabled}
                  className={`relative h-7 w-12 rounded-full transition-colors ${
                    form.payments_enabled ? "bg-emerald-500" : "bg-red-500"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all ${
                      form.payments_enabled ? "left-[22px]" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
              <div className="mt-0.5 text-[12px] text-muted-foreground">
                Disables Razorpay & Manual UPI checkout when off.
              </div>
            </div>
          </div>
          <div className="mt-3">
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
              Disabled message (shown to customers)
            </label>
            <textarea
              value={form.payments_disabled_message}
              onChange={(e) => set("payments_disabled_message", e.target.value)}
              data-testid="admin-payments-disabled-message"
              rows={2}
              placeholder="Payments are temporarily paused. Please try again later."
              className="mt-1 w-full rounded-xl border-2 border-border bg-white px-3 py-2 text-sm font-medium focus:border-navy focus:outline-none resize-none"
            />
          </div>
        </div>

        {/* 2. Dynamic Discount % */}
        <div className="rounded-2xl border-2 border-border p-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-gold/20 text-gold-dark grid place-items-center shrink-0">
              <Percent className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-navy">Dynamic Discount</div>
              <div className="text-[12px] text-muted-foreground">
                Applies to all packages during the promo window.
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                Current
              </div>
              <div className="text-2xl font-extrabold text-gold-dark tabular-nums">
                {form.discount_percent}%
              </div>
            </div>
          </div>
          <div className="mt-3">
            <input
              type="range"
              min="0"
              max="60"
              step="5"
              value={form.discount_percent}
              onChange={(e) => set("discount_percent", Number(e.target.value))}
              data-testid="admin-discount-slider"
              className="w-full accent-gold-dark"
            />
            <div className="flex justify-between text-[10.5px] text-muted-foreground font-bold mt-1">
              <span>0%</span>
              <span>15%</span>
              <span>30%</span>
              <span>45%</span>
              <span>60%</span>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="100"
              value={form.discount_percent}
              onChange={(e) => set("discount_percent", Number(e.target.value || 0))}
              data-testid="admin-discount-input"
              className="w-24 rounded-xl border-2 border-border bg-white px-3 py-2 text-sm font-bold tabular-nums focus:border-navy focus:outline-none"
            />
            <span className="text-[12px] text-muted-foreground">
              0% = no discount, 100% = free.
            </span>
          </div>
        </div>

        {/* 3. Promotional Period (date pickers) */}
        <div className="rounded-2xl border-2 border-border p-4 lg:col-span-2">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-navy/8 text-navy grid place-items-center shrink-0">
              <Calendar className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-navy">Promotional Period</div>
              <div className="text-[12px] text-muted-foreground">
                Discount only applies between Start and End. Leave blank for open-ended.
              </div>
            </div>
            {previewActive && (
              <span
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-extrabold tracking-wider uppercase"
                data-testid="admin-promo-active-pill"
              >
                <Sparkles className="h-3 w-3" /> Live now
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
                Discount Start
              </label>
              <input
                type="datetime-local"
                value={form.discount_start_local}
                onChange={(e) => set("discount_start_local", e.target.value)}
                data-testid="admin-discount-start"
                className="mt-1 w-full rounded-xl border-2 border-border bg-white px-3 py-2 text-sm font-medium focus:border-navy focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
                Discount End
              </label>
              <input
                type="datetime-local"
                value={form.discount_end_local}
                onChange={(e) => set("discount_end_local", e.target.value)}
                data-testid="admin-discount-end"
                className="mt-1 w-full rounded-xl border-2 border-border bg-white px-3 py-2 text-sm font-medium focus:border-navy focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* 4. Promotional Banner */}
        <div className="rounded-2xl border-2 border-border p-4 lg:col-span-2">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-gold/20 text-gold-dark grid place-items-center shrink-0">
              <Megaphone className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <div className="font-extrabold text-navy">Sitewide Promotional Banner</div>
                <button
                  onClick={() => set("banner_enabled", !form.banner_enabled)}
                  data-testid="admin-banner-toggle"
                  role="switch"
                  aria-checked={form.banner_enabled}
                  className={`relative h-7 w-12 rounded-full transition-colors ${
                    form.banner_enabled ? "bg-emerald-500" : "bg-border"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all ${
                      form.banner_enabled ? "left-[22px]" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
              <div className="mt-0.5 text-[12px] text-muted-foreground">
                Banner is visible only when the discount window is active OR payments are paused.
              </div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
                Message (English)
              </label>
              <input
                type="text"
                value={form.banner_message_en}
                onChange={(e) => set("banner_message_en", e.target.value)}
                placeholder="10% Discount until Sunday!"
                data-testid="admin-banner-en"
                className="mt-1 w-full rounded-xl border-2 border-border bg-white px-3 py-2 text-sm font-medium focus:border-navy focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">
                Message (हिंदी)
              </label>
              <input
                type="text"
                value={form.banner_message_hi}
                onChange={(e) => set("banner_message_hi", e.target.value)}
                placeholder="रविवार तक 10% की छूट!"
                data-testid="admin-banner-hi"
                className="mt-1 w-full rounded-xl border-2 border-border bg-white px-3 py-2 text-sm font-medium focus:border-navy focus:outline-none"
              />
            </div>
          </div>

          {/* Live preview of how the banner will look on the site */}
          <div className="mt-3 rounded-xl border-2 border-dashed border-navy/20 p-3 bg-sand-alt/40">
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
              Live Preview
            </div>
            {previewBannerVisible ? (
              <div
                data-testid="admin-banner-preview"
                className={`rounded-md px-4 py-2 text-center font-bold text-[13.5px] ${
                  !form.payments_enabled
                    ? "bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white"
                    : "bg-gradient-to-r from-gold-dark via-gold to-gold-dark text-navy"
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  {form.payments_enabled && <Sparkles className="h-3.5 w-3.5" />}
                  {!form.payments_enabled
                    ? form.payments_disabled_message ||
                      "Payments are temporarily paused. Please try again later."
                    : form.banner_message_en || form.banner_message_hi || "(empty banner)"}
                  {form.payments_enabled && form.discount_percent > 0 && previewActive && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-md bg-navy text-gold text-[11px] font-extrabold tracking-wider">
                      {form.discount_percent}% OFF
                    </span>
                  )}
                </span>
              </div>
            ) : (
              <div className="text-[12px] text-muted-foreground italic flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />
                Banner will appear once the discount window is live or payments are paused.
              </div>
            )}
          </div>
        </div>

        {/* Mobile save button */}
        <div className="lg:col-span-2 sm:hidden">
          <button
            onClick={save}
            disabled={saving}
            data-testid="admin-payment-save-btn-mobile"
            className={`w-full min-h-[52px] rounded-2xl font-extrabold text-[14.5px] flex items-center justify-center gap-2 transition-all ${
              saving
                ? "bg-gold/40 text-navy/60 cursor-not-allowed"
                : "bg-navy text-gold hover:bg-navy-deep"
            }`}
          >
            <Save className="h-5 w-5" />
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Bank Transfer (Interim Payment) — interim option while Razorpay is in approval */}
      <div className="mt-6 rounded-2xl border-2 border-navy/15 bg-sand-alt/40 p-4 sm:p-5" data-testid="admin-bank-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.2em] text-navy/70 font-extrabold">
              Interim payment option
            </div>
            <h3 className="text-[16px] font-extrabold tracking-tight text-navy">
              Bank Transfer / NEFT / IMPS
            </h3>
            <p className="text-[12.5px] text-muted-foreground mt-0.5 max-w-xl">
              Show your bank details on the customer checkout so customers can transfer + upload screenshot.
              You verify manually from <strong>Library Purchases</strong>; the download unlocks automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={() => set("bank_transfer_enabled", !form.bank_transfer_enabled)}
            role="switch"
            aria-checked={!!form.bank_transfer_enabled}
            data-testid="admin-bank-toggle"
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-extrabold border-2 transition-colors ${
              form.bank_transfer_enabled
                ? "bg-emerald-500 text-white border-emerald-700"
                : "bg-white text-navy border-border hover:border-navy"
            }`}
          >
            {form.bank_transfer_enabled ? "Enabled" : "Disabled"}
          </button>
        </div>

        {form.bank_transfer_enabled && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { k: "bank_account_holder", label: "Account holder name", placeholder: "e.g. Team Mera Naksha" },
              { k: "bank_account_number", label: "Account number", placeholder: "e.g. 00012345678901" },
              { k: "bank_ifsc", label: "IFSC code", placeholder: "e.g. HDFC0001234" },
              { k: "bank_name", label: "Bank name", placeholder: "e.g. HDFC Bank, Sasaram Branch" },
            ].map((f) => (
              <label key={f.k} className="block">
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-navy/70 font-bold">{f.label}</span>
                <input
                  value={form[f.k] || ""}
                  onChange={(e) => set(f.k, e.target.value)}
                  data-testid={`admin-${f.k.replace(/_/g, "-")}`}
                  placeholder={f.placeholder}
                  className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
                />
              </label>
            ))}
            <label className="block sm:col-span-2">
              <span className="text-[10.5px] uppercase tracking-[0.18em] text-navy/70 font-bold">Notes (optional)</span>
              <input
                value={form.bank_notes || ""}
                onChange={(e) => set("bank_notes", e.target.value)}
                data-testid="admin-bank-notes"
                placeholder="e.g. Please mention your phone number in the transfer remark."
                className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-border bg-white text-[14px] outline-none focus:border-navy"
              />
            </label>
          </div>
        )}
      </div>
    </section>
  );
};


const MarketingToolkit = ({ onToast }) => {
  const [designs, setDesigns] = useState([]);
  const [selectedByCard, setSelectedByCard] = useState({});

  // Fetch enough designs to populate dropdowns
  useEffect(() => {
    axios
      .get(`${API}/library?page=1&page_size=60`)
      .then(({ data }) => {
        setDesigns(data.items || []);
        // Default-pick the first matching design per card
        const defaults = {};
        for (const card of SHARE_CARDS) {
          const matchById = card.designIdHints
            .map((id) => (data.items || []).find((d) => d.id === id))
            .find(Boolean);
          const matchBySize = !matchById && card.sizeFilter
            ? (data.items || []).find((d) => card.sizeFilter.includes(d.size_label))
            : null;
          const fallback = (data.items || [])[0];
          const pick = matchById || matchBySize || fallback;
          if (pick) defaults[card.id] = pick.id;
        }
        setSelectedByCard(defaults);
      })
      .catch(() => onToast("Failed to load designs", "error"));
  }, [onToast]);

  return (
    <section className="mb-8" data-testid="admin-marketing-toolkit">
      <div className="mb-3">
        <div className="text-[11px] uppercase tracking-[0.2em] text-gold-dark font-bold">
          Launch Campaign
        </div>
        <h2 className="mt-1 text-xl sm:text-2xl font-extrabold text-navy flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-gold-dark" />
          Marketing Toolkit
        </h2>
        <p className="text-sm text-muted-foreground">
          Ready-to-share 1080×1080 cards for WhatsApp Status, Instagram and Facebook.
          Click <b>Download PNG</b> or <b>Share to WhatsApp</b> with the Hindi caption pre-filled.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="share-card-grid">
        {SHARE_CARDS.map((card) => (
          <ShareCardEditor
            key={card.id}
            card={card}
            designs={designs}
            selectedDesignId={selectedByCard[card.id]}
            onPickDesign={(id) =>
              setSelectedByCard((s) => ({ ...s, [card.id]: id }))
            }
            onToast={onToast}
          />
        ))}
      </div>
    </section>
  );
};

const ShareCardEditor = ({ card, designs, selectedDesignId, onPickDesign, onToast }) => {
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const design = useMemo(
    () => designs.find((d) => d.id === selectedDesignId) || null,
    [designs, selectedDesignId]
  );

  const filteredDesigns = useMemo(() => {
    if (!card.sizeFilter) return designs;
    return designs.filter((d) => card.sizeFilter.includes(d.size_label));
  }, [designs, card.sizeFilter]);

  // Render the card whenever design changes
  useEffect(() => {
    if (!canvasRef.current) return;
    drawShareCard(canvasRef.current, card, design).catch(() => {
      // Render text-only version if image fails
      drawShareCard(canvasRef.current, card, null);
    });
  }, [card, design]);

  const downloadPng = async () => {
    setBusy(true);
    try {
      // Re-render to ensure freshness, then download
      await drawShareCard(canvasRef.current, card, design);
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `mera-naksha-${card.id}.png`;
      a.click();
      onToast("PNG downloaded");
    } catch {
      onToast("Download failed", "error");
    } finally {
      setBusy(false);
    }
  };

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(card.caption_hi)}`;
    window.open(url, "_blank", "noopener");
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(card.caption_hi);
      onToast("Caption copied");
    } catch {
      onToast("Copy failed", "error");
    }
  };

  return (
    <article
      className="rounded-2xl bg-white border-2 border-border overflow-hidden shadow-sm hover:border-gold/40 transition-colors flex flex-col"
      data-testid={`share-card-${card.id}`}
    >
      <div className="bg-navy text-white p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PhotoIcon className="h-4 w-4 text-gold" />
          <div className="font-bold text-[14px]">{card.title}</div>
        </div>
        {card.price && (
          <span className="px-2 py-0.5 rounded-full bg-gold text-navy text-[11px] font-extrabold">
            {card.price}
          </span>
        )}
      </div>

      {/* Live preview (canvas itself, scaled down via CSS) */}
      <div className="bg-sand-alt p-3 grid place-items-center" data-testid={`share-card-preview-${card.id}`}>
        <canvas
          ref={canvasRef}
          className="w-full max-w-[320px] aspect-square rounded-xl shadow-md bg-navy"
          aria-label={`${card.title} preview`}
        />
      </div>

      <div className="p-3 space-y-2.5 flex-1 flex flex-col">
        {/* Design picker (only if there are matching designs) */}
        {filteredDesigns.length > 0 && (
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-bold">
              Design used
            </span>
            <select
              value={selectedDesignId || ""}
              onChange={(e) => onPickDesign(e.target.value)}
              data-testid={`share-card-design-${card.id}`}
              className="lib-input mt-1"
            >
              {filteredDesigns.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.size_label}) · ₹{d.price}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="rounded-xl bg-sand-alt border border-border p-2.5 text-[12px] text-foreground">
          <div className="font-semibold text-navy">{card.headline_hi}</div>
          <div className="text-muted-foreground mt-0.5">{card.sub_hi}</div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-auto">
          <button
            onClick={downloadPng}
            disabled={busy}
            data-testid={`share-card-download-${card.id}`}
            className="min-h-[42px] rounded-lg bg-navy text-gold font-bold text-[12px] flex items-center justify-center gap-1 hover:bg-navy-deep disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" /> PNG
          </button>
          <button
            onClick={copyCaption}
            data-testid={`share-card-copy-${card.id}`}
            className="min-h-[42px] rounded-lg bg-white border-2 border-navy text-navy font-bold text-[12px] flex items-center justify-center gap-1 hover:bg-sand-alt"
          >
            <Copy className="h-3.5 w-3.5" /> Caption
          </button>
          <button
            onClick={shareWhatsApp}
            data-testid={`share-card-whatsapp-${card.id}`}
            className="min-h-[42px] rounded-lg bg-whatsapp text-white font-bold text-[12px] flex items-center justify-center gap-1 hover:bg-whatsapp-hover shadow-[0_4px_10px_-4px_rgba(37,211,102,0.5)]"
          >
            <FaWhatsapp className="h-3.5 w-3.5" /> Share
          </button>
        </div>
      </div>
    </article>
  );
};

// =============== Receipt Review Modal ===============
// Renders a single purchase screenshot (UPI or Bank Transfer) with the
// customer's submitted UPI/UTR reference + Approve/Reject CTAs. Used by the
// NotificationBell to power the 1-click manual-payment verification flow.
const ReceiptReviewModal = ({ receipt, onClose, onApprove, onReject, verifying }) => {
  if (!receipt) return null;
  const isBank = receipt.payment_method === "bank_transfer";
  const methodLabel = isBank ? "Bank Transfer / NEFT" : "UPI";
  const screenshot = receipt.screenshot_url;
  const isVerified = receipt.payment_status === "verified";
  const isRejected = receipt.payment_status === "rejected";
  const customerWa = (receipt.customer_whatsapp || "").replace(/^\+/, "");
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="receipt-review-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white rounded-2xl border-2 border-gold shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        data-testid="receipt-review-modal"
      >
        <div className="bg-navy text-white p-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-gold font-bold">
              Manual Payment Review
            </div>
            <div className="font-extrabold text-[17px] mt-0.5">
              Order #{receipt.receipt_no}
            </div>
            <div className="text-[12px] text-white/70 mt-1">
              {receipt.customer_name || "—"} · {receipt.customer_whatsapp || "—"}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="receipt-review-close"
            className="h-8 w-8 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 space-y-3 bg-sand-alt/60">
          <div className="grid grid-cols-2 gap-2 text-[12px]">
            <div className="rounded-lg bg-white border border-border p-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                Method
              </div>
              <div
                data-testid="receipt-method-label"
                className={`mt-0.5 font-extrabold text-[13px] ${
                  isBank ? "text-navy" : "text-emerald-700"
                }`}
              >
                {methodLabel}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-border p-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                Amount
              </div>
              <div className="mt-0.5 font-extrabold text-[13px] text-navy">
                ₹{(receipt.amount || 0).toLocaleString("en-IN")}
              </div>
            </div>
            <div className="rounded-lg bg-white border border-border p-2 col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                Design
              </div>
              <div className="mt-0.5 font-bold text-[13px] text-navy">
                {receipt.design_name} · {receipt.design_size}
              </div>
            </div>
            {receipt.upi_reference && (
              <div className="rounded-lg bg-white border border-border p-2 col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                  {isBank ? "UTR / Transaction Ref" : "UPI Ref"}
                </div>
                <div
                  data-testid="receipt-ref-value"
                  className="mt-0.5 font-mono font-bold text-[13px] text-navy break-all"
                >
                  {receipt.upi_reference}
                </div>
              </div>
            )}
            <div className="rounded-lg bg-white border border-border p-2 col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                Status
              </div>
              <div className="mt-0.5 font-bold text-[13px] text-navy capitalize">
                {(receipt.payment_status || "").replace(/_/g, " ")}
              </div>
            </div>
          </div>

          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-extrabold mb-1">
              Payment screenshot
            </div>
            {screenshot ? (
              <div className="rounded-xl border-2 border-border overflow-hidden bg-white">
                <img
                  src={screenshot}
                  alt="Payment screenshot"
                  data-testid="receipt-screenshot-img"
                  className="block w-full max-h-[50vh] object-contain bg-sand-alt"
                />
              </div>
            ) : (
              <div className="rounded-xl border-2 border-dashed border-border p-6 text-center text-[12px] text-muted-foreground bg-white">
                No screenshot uploaded yet.
              </div>
            )}
          </div>

          {customerWa && (
            <a
              href={`https://wa.me/${customerWa}?text=${encodeURIComponent(
                `Hello ${receipt.customer_name || ""}, this is Team Mera Naksha regarding Order #${receipt.receipt_no}.`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="receipt-wa-customer"
              className="block text-center w-full min-h-[40px] rounded-lg bg-whatsapp text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5 hover:bg-whatsapp-hover"
            >
              <FaWhatsapp className="h-4 w-4" />
              WhatsApp customer
            </a>
          )}
        </div>

        <div className="p-3 border-t-2 border-border bg-white flex items-center gap-2">
          {isVerified || isRejected ? (
            <div
              data-testid="receipt-decision-banner"
              className={`flex-1 text-center text-[12.5px] font-extrabold rounded-lg py-2 ${
                isVerified
                  ? "bg-emerald-50 text-emerald-700 border-2 border-emerald-300"
                  : "bg-red-50 text-red-700 border-2 border-red-300"
              }`}
            >
              {isVerified ? "Already verified — plan unlocked." : "Already rejected."}
            </div>
          ) : (
            <>
              <button
                onClick={onReject}
                disabled={verifying}
                data-testid="receipt-reject-btn"
                className="min-h-[44px] px-4 rounded-xl border-2 border-red-300 text-red-700 font-extrabold text-[13px] hover:bg-red-50 disabled:opacity-60"
              >
                Reject
              </button>
              <button
                onClick={onApprove}
                disabled={verifying}
                data-testid="receipt-approve-btn"
                className="flex-1 min-h-[44px] rounded-xl bg-gold text-navy font-extrabold text-[14px] hover:bg-gold-dark hover:text-white disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
              >
                {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Approve & unlock plan
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// =============== Notification Bell ===============
const NotificationBell = ({ onToast }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0, admin_whatsapp: "" });
  const [activeReceipt, setActiveReceipt] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notifications?limit=20`);
      setData(data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("notif fetch failed", e);
    }
  }, []);

  // Initial + 15s polling
  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, 15000);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  const markRead = async (notif) => {
    if (notif.is_read) return;
    try {
      await axios.post(`${API}/admin/notifications/${notif.id}/read`);
      fetchNotifications();
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("notif mark-read failed", e);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/admin/notifications/read-all`);
      fetchNotifications();
      onToast?.("All notifications marked read");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("notif mark-all-read failed", e);
    }
  };

  const adminWa = data.admin_whatsapp || "919717660160";

  const handleVerify = async (decision) => {
    if (!activeReceipt) return;
    const reason = decision === "rejected"
      ? (window.prompt("Optional rejection note (visible to customer):") || "")
      : "";
    setVerifying(true);
    try {
      await axios.post(
        `${API}/admin/purchases/${activeReceipt.receipt_no}/verify`,
        { decision, admin_note: reason || undefined }
      );
      onToast?.(
        decision === "verified"
          ? `Order #${activeReceipt.receipt_no} verified — plan unlocked.`
          : `Order #${activeReceipt.receipt_no} marked rejected.`,
        decision === "verified" ? "success" : "info"
      );
      setActiveReceipt(null);
      fetchNotifications();
    } catch {
      onToast?.("Verification failed — try again", "error");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="relative">
      {activeReceipt && (
        <ReceiptReviewModal
          receipt={activeReceipt}
          onClose={() => setActiveReceipt(null)}
          onApprove={() => handleVerify("verified")}
          onReject={() => handleVerify("rejected")}
          verifying={verifying}
        />
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="admin-notif-bell"
        className="relative inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white/10 hover:bg-white/20 text-white border border-white/15"
        aria-label={`Notifications (${data.unread} unread)`}
      >
        <Bell className="h-5 w-5" />
        {data.unread > 0 && (
          <span
            data-testid="notif-unread-badge"
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-gold text-navy text-[11px] font-extrabold grid place-items-center border-2 border-navy animate-pulse"
          >
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className="absolute right-0 top-12 z-50 w-[min(92vw,380px)] rounded-2xl bg-white border-2 border-gold shadow-2xl overflow-hidden text-foreground animate-fade-up"
            data-testid="notif-panel"
          >
            <div className="bg-navy text-white p-3 flex items-center justify-between">
              <div className="font-extrabold text-[14px] flex items-center gap-2">
                <Bell className="h-4 w-4 text-gold" />
                Admin Alerts
                {data.unread > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-gold text-navy text-[10px] font-extrabold">
                    {data.unread} new
                  </span>
                )}
              </div>
              <button
                onClick={markAllRead}
                data-testid="notif-mark-all"
                className="text-[11px] text-gold hover:text-white font-bold"
              >
                Mark all read
              </button>
            </div>

            {data.items.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-[13px]" data-testid="notif-empty">
                No alerts yet. New payment screenshots will appear here.
              </div>
            ) : (
              <ul className="max-h-[60vh] overflow-y-auto divide-y divide-border" data-testid="notif-list">
                {data.items.map((n) => (
                  <li
                    key={n.id}
                    className={`p-3 hover:bg-sand-alt transition-colors ${
                      n.is_read ? "" : "bg-gold/5"
                    }`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 ${
                        n.is_read ? "bg-sand-alt text-muted-foreground" : "bg-gold text-navy"
                      }`}>
                        <Bell className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-[13px] text-navy truncate">
                          {n.title}
                        </div>
                        <div className="text-[11.5px] text-muted-foreground mt-0.5 leading-snug">
                          {n.body}
                        </div>
                        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                          <a
                            href={`https://wa.me/${adminWa}?text=${encodeURIComponent(n.wa_message)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => markRead(n)}
                            data-testid={`notif-wa-${n.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-whatsapp text-white text-[10.5px] font-bold hover:bg-whatsapp-hover"
                          >
                            <FaWhatsapp className="h-3 w-3" />
                            WhatsApp
                            <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                          {n.receipt_no && (
                            <button
                              onClick={async () => {
                                try {
                                  const { data } = await axios.get(
                                    `${API}/admin/purchases/${n.receipt_no}`
                                  );
                                  setActiveReceipt(data);
                                  markRead(n);
                                } catch {
                                  onToast?.("Could not load receipt", "error");
                                }
                              }}
                              data-testid={`notif-review-${n.id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gold text-navy text-[10.5px] font-bold hover:bg-gold-dark hover:text-white"
                            >
                              Review screenshot
                            </button>
                          )}
                          {!n.is_read && (
                            <button
                              onClick={() => markRead(n)}
                              data-testid={`notif-mark-${n.id}`}
                              className="px-2 py-1 rounded-md border border-border text-navy text-[10.5px] font-bold hover:bg-sand-alt"
                            >
                              Mark read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="bg-sand-alt border-t border-border px-3 py-2 text-[10.5px] text-muted-foreground text-center">
              Routes alerts to <span className="font-mono font-bold text-navy">+{adminWa.replace(/^91/, '91 ')}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
